import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import { logApiError, getUserFriendlyErrorMessage } from './errorLogger';
import {
  User,
  Project,
  Agent,
  Model,
  Workflow,
  WorkflowStep,
  RunInstance,
  RunResult,
  RunResultsWithSession,
  SessionMessage,
  CreateProjectForm,
  CreateAgentForm,
  CreateModelForm,
  CreateWorkflowForm,
  ApiResponse,
  PaginatedResponse,
  AgentTool,
  RunStatus,
  AssistantChatRequest,
  AssistantChatResponse,
  AssistantConversationCreate,
  AssistantConversation,
  AssistantConversationWithMessages,
  AssistantConversationUpdate,
  KnowledgeBaseCreate,
  KnowledgeBase,
  KnowledgeBaseUpdate,
  KnowledgeSearchRequest,
  KnowledgeSearchResult,
  DocumentUploadResponse,
  CheckDuplicatesResponse,
  ProjectDocumentsResponse,
  AllowedStepIntentOption,
  AllowedIntentsRequest,
  StepType,
  StepIntent,
  ClearSessionRequest,
  ClearSessionResponse,
  GoDaddyCredentialsCreate,
  GoDaddyCredentialsStatus,
  GoDaddyTestConnectionRequest,
  GoDaddyTestConnectionResponse,
  AgentTemplate,
  AgentTemplateList,
  PurchaseRules,
  PurchaseRulesResponse,
  RegistryEnabledModel,
  RegistryConfigResponse,
  RegistryConfigUpdate,
  RegistryEntriesListResponse,
  RegistryEntriesParams,
  RegistryEntryResponse,
  RegistryStatsResponse,
  CircuitBreakerState,
  CircuitBreakerSettings,
  QueueUpdateSettings,
  Queue,
  HistogramStatus,
  PriorityHistogramResponse,
  PayloadFieldsResponse,
  QueueConsumer,
  QueueConsumerConfig,
  ConsumerRegisterRequest,
  QueueItem,
  QueueStats,
  Schedule,
  ScheduleCreate,
  ScheduleUpdate,
  SchedulePreviewRun,
  TimePeriod,
  Granularity,
  SpendingAnalyticsSummary,
  SpendingTrendData,
  TLDDistributionData,
  // Token Usage types (AC-604)
  UsageQueryParams,
  TokenUsageSummary,
  TokenUsageByModelResponse,
  TokenUsageByProviderResponse,
  TokenUsageByWorkflowResponse,
  TokenUsageBySourceResponse,
  TokenUsageTrend,
  UserBudget,
  BudgetConfig,
  BudgetStatus,
  // Hierarchical cost breakdown types (AC-622)
  TokenUsageByProjectResponse,
  TokenUsageByAgentResponse,
  HierarchicalUsageQueryParams,
  // MCP (Model Context Protocol) types (AC-778, AC-785)
  MCPServer,
  MCPServerCreate,
  MCPServerUpdate,
  MCPToolInfo,
  MCPTestConnectionResponse,
  MCPServerTemplate,
  MCPServersAvailableResponse,
  // Assistant MCP discovery / provisioning types (AC-1738, epic AC-1732)
  McpProvisionRequest,
  // Assistant built-in configure types (AC-1746, defect AC-1743, epic AC-1732)
  McpConfigureBuiltinRequest,
  // Assistant concierge schedule-setup types (AC-1790, epic AC-1787)
  ScheduleConfirmRequest,
  // Social / Sharing types (AC-856)
  SharedProjectCard,
  SharedProjectDetail,
  SharedProjectListResponse,
  UserPublicProfile,
  UserPublicSummary,
  UserListResponse,
  FollowResponse,
  SharedToolSummary,
  SharedAgentSummary,
  SharedWorkflowSummary,
  SharedWorkflowDetail,
  SharedModelSummary,
  // Phase 2 types (AC-856)
  PublishedVersionSummary,
  PublishedVersionListResponse,
  PublishedVersionDetail,
  // Team types (AC-888)
  TeamCreate,
  TeamUpdate,
  TeamResponse,
  TeamDetail,
  TeamListResponse,
  TeamMemberListResponse,
  TeamInvitation,
  TeamInvitationListResponse,
  // Admin / Allowlist types (AC-915)
  AllowlistEntry,
  AdminUserSummary,
  // Admin Token Usage types
  AdminPlatformUsageSummary,
  AdminUserUsageSummary,
  AdminUserUsageListResponse,
  // Built-in Email / MCP Credential types (AC-1108)
  BuiltinCredentialStatus,
  EmailProviderPreset,
  SaveCredentialRequest,
  TestConnectionResult,
  // Slack built-in setup preset (AC-1807, epic AC-1803)
  SlackSetupPreset,
  // WhatsApp Integration types (AC-1050)
  WhatsAppStatus,
  WhatsAppConnectionResponse,
  SlackLinkStartResponse,
  SlackLinkStatusResponse,
  SlackConfigResponse,
  WhatsAppQRResponse,
  WhatsAppPairingCodeResponse,
  WhatsAppNotificationPreferences,
  WhatsAppNotificationPreferencesUpdate,
  // Instruction Assistant types (AC-1189)
  InstructionGenerateRequest,
  InstructionImproveRequest,
  InstructionRefineRequest,
  InstructionAssistantResponse,
  // LLM Catalog types (AC-1347)
  LLMCatalogResponse,
  // Assistant model selection (AC-1616)
  AssistantModelsResponse,
  AssistantModelPreference,
  // BYOK user-credentials (AC-1493)
  UserApiKey,
  CreateApiKeyRequest,
  ApiKeyTestResult,
  LLMProvider,
  // System mode (AC-1619)
  SystemMode,
  // Workflow delivery channels (AC-1816, epic AC-1813)
  ChannelAvailability,
  DeliveryChannelAvailabilityResponse,
} from '../types';
import {
  CheckpointSummary,
  CheckpointDetail,
  CheckpointCreate,
  CheckpointListResponse,
  RestoreResponse,
  CompareResponse,
  ChangeSummary,
  ChangeDetail,
  ChangeListResponse,
  ChangeFilters,
  ReapplyResponse,
  UndoResponse,
  RevertToResponse,
  RetentionSettings,
  RetentionSettingsUpdate,
  ChangeEntityType,
} from '../types/version_control';
import {
  TutorialProgressResponse,
  TutorialProgressUpdate,
} from '../types/tutorial';
import { RDAPToolDefinitionsResponse } from '../types/rdap';

// Enhanced error types for better async error handling
export interface ApiErrorResponse {
  message: string;
  code?: string;
  details?: any;
  retryable?: boolean;
  timestamp?: string;
}

export class ApiError extends Error {
  public code?: string;
  public details?: any;
  public retryable: boolean;
  public status?: number;

  constructor(message: string, code?: string, details?: any, retryable = false, status?: number) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.details = details;
    this.retryable = retryable;
    this.status = status;
  }
}

// Request queue for handling concurrent requests
interface QueuedRequest {
  id: string;
  request: () => Promise<any>;
  resolve: (value: any) => void;
  reject: (error: any) => void;
  retryCount: number;
  maxRetries: number;
  priority: number;
}

class ApiService {
  private api: AxiosInstance;
  private token: string | null = null;
  private refreshToken: string | null = null;
  private tokenExpiry: number | null = null;
  private isRefreshing = false;
  private failedQueue: Array<{
    resolve: (value?: any) => void;
    reject: (error?: any) => void;
  }> = [];
  private currentProjectId: string | null = null;

  // Enhanced async capabilities
  private requestQueue: QueuedRequest[] = [];
  private isProcessingQueue = false;
  private maxConcurrentRequests = 5;
  private activeRequests = 0;
  private requestTimeout = 120000; // 2 minutes (120 seconds)
  private retryConfig = {
    maxRetries: 3,
    baseDelay: 1000,
    maxDelay: 10000,
    backoffMultiplier: 2,
  };

  // Cache for allowed intents (short-lived to ensure fresh validation data)
  private allowedIntentsCache = new Map<string, {
    data: AllowedStepIntentOption[];
    timestamp: number;
  }>();
  private allowedIntentsCacheTtl = 5 * 60 * 1000; // 5 minutes

  constructor() {
    // Use empty baseURL to leverage Vite proxy in development
    // Set VITE_API_URL only when you need direct API access (e.g., production build)
    this.api = axios.create({
      baseURL: import.meta.env.VITE_API_URL || '',
      timeout: this.requestTimeout,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token and check expiration
    this.api.interceptors.request.use(
      async (config) => {
        // Check if token is about to expire (within 5 minutes)
        if (this.token && this.tokenExpiry && this.refreshToken) {
          const now = Date.now();
          const timeUntilExpiry = this.tokenExpiry - now;
          const fiveMinutes = 5 * 60 * 1000;

          if (timeUntilExpiry < fiveMinutes && timeUntilExpiry > 0) {
            try {
              await this.refreshAccessToken();
            } catch (error) {
              console.warn('Failed to refresh token proactively:', error);
            }
          }
        }

        if (this.token) {
          config.headers.Authorization = `Bearer ${this.token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling and token refresh
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Log the error with detailed information, unless the caller opted out
        // (e.g. for endpoints where 404 is an expected "no-resource" response
        // and shouldn't clutter the console).
        if (!originalRequest?._suppressErrorLog) {
          logApiError(
            error,
            originalRequest?.url || 'unknown',
            originalRequest?.method?.toUpperCase() || 'GET',
            originalRequest?.data
          );
        }

        // AC-1363: skip the refresh-and-retry path when there's no original
        // request to retry (e.g. cancelled axios requests, SSE adapter calls).
        // Without this guard, accessing originalRequest._retry would throw
        // and turn a transient 401 into an unhandled promise rejection.
        if (originalRequest && error.response?.status === 401 && !originalRequest._retry) {
          if (this.isRefreshing) {
            // If already refreshing, queue the request
            return new Promise((resolve, reject) => {
              this.failedQueue.push({ resolve, reject });
            }).then(() => {
              originalRequest.headers.Authorization = `Bearer ${this.token}`;
              return this.api(originalRequest);
            }).catch(err => Promise.reject(err));
          }

          originalRequest._retry = true;

          if (this.refreshToken) {
            try {
              await this.refreshAccessToken();
              originalRequest.headers.Authorization = `Bearer ${this.token}`;
              this.processQueue(null);
              return this.api(originalRequest);
            } catch (refreshError) {
              this.processQueue(refreshError);
              this.handleAuthError();
              return Promise.reject(refreshError);
            }
          } else {
            this.handleAuthError();
          }
        }

        return Promise.reject(error);
      }
    );

    // Load tokens from localStorage
    this.loadTokens();
  }

  private processQueue(error: any) {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else {
        resolve();
      }
    });
    this.failedQueue = [];
  }

  private handleAuthError() {
    this.clearTokens();
    // Show user-friendly notification instead of immediate redirect
    if (window.location.pathname !== '/login') {
      console.warn('Session expired. Please log in again.');
      window.location.href = '/login';
    }
  }

  private loadTokens() {
    const token = localStorage.getItem('auth_token');
    const refreshToken = localStorage.getItem('refresh_token');
    const tokenExpiry = localStorage.getItem('token_expiry');
    
    if (token) {
      this.token = token;
    }
    if (refreshToken) {
      this.refreshToken = refreshToken;
    }
    if (tokenExpiry) {
      this.tokenExpiry = parseInt(tokenExpiry, 10);
    }
  }

  private loadToken() {
    const token = localStorage.getItem('auth_token');
    if (token) {
      this.setToken(token);
    }
  }

  setToken(token: string) {
    this.token = token;
    localStorage.setItem('auth_token', token);
  }

  /**
   * Read the current access token. Used by transports that aren't axios
   * (e.g. the SSE stream in runStatusStream.ts) so they can attach the same
   * bearer that interceptors would add automatically.
   */
  getAccessToken(): string | null {
    return this.token;
  }

  setTokens(accessToken: string, refreshToken?: string, expiresIn?: number) {
    this.token = accessToken;
    localStorage.setItem('auth_token', accessToken);
    
    if (refreshToken) {
      this.refreshToken = refreshToken;
      localStorage.setItem('refresh_token', refreshToken);
    }
    
    if (expiresIn) {
      // Calculate expiry time (current time + expires_in seconds)
      this.tokenExpiry = Date.now() + (expiresIn * 1000);
      localStorage.setItem('token_expiry', this.tokenExpiry.toString());
    }
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem('auth_token');
  }

  clearTokens() {
    this.token = null;
    this.refreshToken = null;
    this.tokenExpiry = null;
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('token_expiry');
  }

  private async refreshAccessToken(): Promise<void> {
    if (!this.refreshToken) {
      throw new Error('No refresh token available');
    }

    this.isRefreshing = true;

    try {
      const response = await axios.post(`/refresh?refresh_token=${encodeURIComponent(this.refreshToken)}`, {}, {
        baseURL: import.meta.env.VITE_API_URL || '',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const { access_token, refresh_token, expires_in } = response.data;
      this.setTokens(access_token, refresh_token, expires_in);
    } finally {
      this.isRefreshing = false;
    }
  }

  // Authentication
  async login(username: string, password: string): Promise<{ access_token: string; refresh_token?: string; token_type: string; expires_in?: number }> {
    const formData = new FormData();
    formData.append('username', username);
    formData.append('password', password);

    const response = await this.api.post('/login', formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
    
    const { access_token, refresh_token, expires_in } = response.data;
    this.setTokens(access_token, refresh_token, expires_in);
    
    return response.data;
  }

  async register(username: string, password: string, email?: string): Promise<{ access_token: string; refresh_token?: string; token_type: string; expires_in?: number }> {
    const response = await this.api.post('/register', {
      username,
      password,
      email,
    });
    
    const { access_token, refresh_token, expires_in } = response.data;
    this.setTokens(access_token, refresh_token, expires_in);
    
    return response.data;
  }

  async getCurrentUser(): Promise<User> {
    const response = await this.api.get('/api/v1/users/me');
    return response.data;
  }

  // Projects
  async getProjects(): Promise<Project[]> {
    const response = await this.api.get('/api/v1/projects');
    return response.data;
  }

  async getOperation(projectId: string): Promise<Project> {
    const response = await this.api.get(`/api/v1/projects/${projectId}`);
    return response.data;
  }

  async createProject(data: CreateProjectForm): Promise<Project> {
    const response = await this.api.post('/api/v1/projects', data);
    return response.data;
  }

  async updateProject(projectId: string, data: Partial<CreateProjectForm> & { is_public?: boolean; visibility?: string; team_ids?: number[] }): Promise<Project> {
    const response = await this.api.put(`/api/v1/projects/${projectId}`, data);
    return response.data;
  }

  async deleteProject(projectId: string): Promise<void> {
    await this.api.delete(`/api/v1/projects/${projectId}`);
  }

  /**
   * AC-1599 / AC-1594: Clone a project (deep-copy of agents, models, workflows,
   * etc. into a brand-new project owned by the current user).
   *
   * Calls `POST /api/v1/projects/{id}/clone` (AC-1597 backend endpoint).
   *
   * @param projectId - Source project id. Accepts string (router params) or
   *                    number (store ids); the value is URL-interpolated
   *                    verbatim, matching the apiService pattern for
   *                    `copyWorkflow` and similar widened methods.
   * @param payload   - `{ name }` per the backend's `ProjectCloneRequest`
   *                    schema. Other fields are NOT accepted.
   * @returns The newly-created project row (full `Project` shape).
   *
   * Errors: 404 (source project not found / not owned by current user),
   * 409 (a project named `payload.name` already exists for this user),
   * 500 (unexpected server error).
   */
  async cloneProject(
    projectId: string | number,
    payload: { name: string }
  ): Promise<Project> {
    const response = await this.api.post(
      `/api/v1/projects/${projectId}/clone`,
      payload
    );
    return response.data;
  }

  // Agents
  async getAgents(projectId: string): Promise<Agent[]> {
    const response = await this.api.get(`/api/v1/projects/${projectId}/agents`);
    return response.data;
  }

  // New: Get all agents for the user
  async getAllAgents(): Promise<Agent[]> {
    const response = await this.api.get(`/api/v1/agents`);
    return response.data;
  }

  async getAgent(agentId: string): Promise<Agent> {
    const response = await this.api.get(`/api/v1/agents/${agentId}`);
    return response.data;
  }

  async createAgent(projectId: string, data: CreateAgentForm): Promise<Agent> {
    const response = await this.api.post(`/api/v1/projects/${projectId}/agents`, {
      ...data,
      project_id: projectId,
    });
    return response.data;
  }

  async updateAgent(agentId: string, data: Partial<CreateAgentForm>): Promise<Agent> {
    const response = await this.api.put(`/api/v1/agents/${agentId}`, data);
    return response.data;
  }

  async deleteAgent(agentId: string): Promise<void> {
    await this.api.delete(`/api/v1/agents/${agentId}`);
  }

  async getAgentWorkflows(agentId: string): Promise<{ id: number; name: string }[]> {
    const response = await this.api.get(`/api/v1/agents/${agentId}/workflows`);
    return response.data;
  }

  async addAgentTool(agentId: string, tool: AgentTool): Promise<AgentTool> {
    const response = await this.api.post(`/api/v1/agents/${agentId}/tools`, tool);
    return response.data;
  }

  async removeAgentTool(agentId: string, toolId: string): Promise<void> {
    await this.api.delete(`/api/v1/agents/${agentId}/tools/${toolId}`);
  }

  // Models
  async getModels(projectId: string): Promise<Model[]> {
    const response = await this.api.get(`/api/v1/projects/${projectId}/models`);
    return response.data;
  }

  // New: Get all models for the user
  async getAllModels(): Promise<Model[]> {
    const response = await this.api.get(`/api/v1/models`);
    return response.data;
  }

  // LLM Catalog (AC-1347)
  async getLLMCatalog(): Promise<LLMCatalogResponse> {
    const response = await this.api.get('/api/v1/models/llm-catalog');
    return response.data;
  }

  // Assistant model selection (AC-1616, epic AC-1609).
  //
  // The assistant runs on a key-gated, mid-tier+ allow-list distinct from the
  // full agent catalog. `getAssistantModels` returns that allow-list plus the
  // server's fallback `default`; the preference endpoints read/write the
  // current user's saved choice (the backend rejects a non-allow-listed model
  // with a 400).
  async getAssistantModels(): Promise<AssistantModelsResponse> {
    const response = await this.api.get('/api/v1/assistant/models');
    return response.data;
  }

  async getAssistantModelPreference(): Promise<AssistantModelPreference> {
    const response = await this.api.get('/api/v1/assistant/model-preference');
    return response.data;
  }

  async setAssistantModelPreference(
    model: string,
  ): Promise<AssistantModelPreference> {
    const response = await this.api.put('/api/v1/assistant/model-preference', {
      model,
    });
    return response.data;
  }

  // System mode (AC-1619).
  //
  // Reports whether the deployment honors per-user BYOK keys (`byok_mode`
  // true) or is in "Friends & Family" / platform-managed mode (`byok_mode`
  // false; per-user keys ignored, platform env keys used instead). The API
  // Keys settings page uses this to decide whether to show the Add-key CTA
  // and the platform-managed banner.
  async getSystemMode(): Promise<SystemMode> {
    const response = await this.api.get('/api/v1/system/mode');
    return response.data;
  }

  // BYOK user-credentials (AC-1493 Phase 5).
  //
  // The backend list endpoint returns the full set of providers (one row per
  // provider) so the UI can render a card for every provider — including
  // those the user has NOT configured yet — without a separate "supported
  // providers" call. Each row carries `has_key` plus the (non-secret)
  // metadata (display_label, last_validated_at, created_at). The raw key is
  // never returned by GET. As of AC-1619 each row also carries
  // `platform_configured`.
  async listApiKeys(): Promise<UserApiKey[]> {
    // Backend response shape is `UserApiKeyListResponse = { providers: [...] }`
    // (FastAPI wrapping). Unwrap to the flat array consumers expect.
    const response = await this.api.get<{ providers: UserApiKey[] }>(
      '/api/v1/credentials',
    );
    return response.data?.providers ?? [];
  }

  async createApiKey(
    provider: LLMProvider,
    data: CreateApiKeyRequest,
  ): Promise<UserApiKey> {
    const response = await this.api.post(
      `/api/v1/credentials/${provider}`,
      data,
    );
    return response.data;
  }

  async updateApiKey(
    provider: LLMProvider,
    data: CreateApiKeyRequest,
  ): Promise<UserApiKey> {
    const response = await this.api.put(
      `/api/v1/credentials/${provider}`,
      data,
    );
    return response.data;
  }

  async deleteApiKey(provider: LLMProvider): Promise<void> {
    await this.api.delete(`/api/v1/credentials/${provider}`);
  }

  async testApiKey(provider: LLMProvider): Promise<ApiKeyTestResult> {
    const response = await this.api.post(
      `/api/v1/credentials/${provider}/test`,
    );
    return response.data;
  }

  async getModel(modelId: number): Promise<Model> {
    const response = await this.api.get(`/api/v1/models/${modelId}`);
    return response.data;
  }

  async getAgentsUsingModel(modelId: number): Promise<Agent[]> {
    const response = await this.api.get(`/api/v1/models/${modelId}/agents`);
    return response.data;
  }

  async createModel(projectId: string, data: CreateModelForm): Promise<Model> {
    const response = await this.api.post(`/api/v1/projects/${projectId}/models`, data);
    return response.data;
  }

  async updateModel(modelId: number, data: Partial<CreateModelForm>): Promise<Model> {
    const response = await this.api.put(`/api/v1/projects/${this.currentProjectId}/models/${modelId}`, data);
    return response.data;
  }

  async deleteModel(modelId: number): Promise<void> {
    await this.api.delete(`/api/v1/models/${modelId}`);
  }

  // Workflows
  async getWorkflows(projectId: string): Promise<Workflow[]> {
    const response = await this.api.get(`/api/v1/orchestrations/project/${projectId}`);
    return response.data;
  }

  // New: Get all workflows for the user
  async getAllWorkflows(): Promise<Workflow[]> {
    const response = await this.api.get(`/api/v1/orchestrations`);
    return response.data;
  }

  async getWorkflow(workflowId: string): Promise<Workflow> {
    const response = await this.api.get(`/api/v1/orchestrations/${workflowId}`);
    return response.data;
  }

  async createWorkflow(projectId: string, data: CreateWorkflowForm): Promise<Workflow> {
    const response = await this.api.post(`/api/v1/orchestrations/project/${projectId}`, data);
    return response.data;
  }

  async updateWorkflow(workflowId: string, data: Partial<CreateWorkflowForm>): Promise<Workflow> {
    const response = await this.api.put(`/api/v1/orchestrations/${workflowId}`, data);
    return response.data;
  }

  /**
   * AC-1816 (epic AC-1813). Per-channel delivery availability for the current
   * user, derived server-side from existing credential status (WhatsApp
   * connected?, Slack/Email built-in credential present?). Drives the
   * DeliveryChannelSetting selector: only `available` channels are offered;
   * unavailable channels are disabled and surface their `reason` + a setup hint.
   *
   * @returns The flat `ChannelAvailability[]` (unwrapped from the
   *          `{ channels: [...] }` response) for direct use as the
   *          `availableChannels` prop.
   */
  async getDeliveryChannelAvailability(): Promise<ChannelAvailability[]> {
    const response = await this.api.get<DeliveryChannelAvailabilityResponse>(
      '/api/v1/delivery/channels'
    );
    return response.data.channels;
  }

  async deleteWorkflow(workflowId: string): Promise<void> {
    await this.api.delete(`/api/v1/orchestrations/${workflowId}`);
  }

  /**
   * AC-1598 / AC-1594: Copy a workflow into the same or a different project.
   *
   * Calls `POST /api/v1/orchestrations/{id}/copy` (AC-1596 backend endpoint).
   *
   * @param workflowId - Source workflow id. Accepts string (router params)
   *                     or number (store ids); the value is URL-interpolated
   *                     verbatim, matching the apiService pattern for other
   *                     workflow methods.
   * @param payload    - `{ name, target_project_id? }` per the backend's
   *                     `OrchestrationCopyRequest` schema. `target_project_id`
   *                     may be omitted/null to copy within the source project.
   * @returns The newly-created workflow row (full `Workflow` shape).
   *
   * Errors: backend returns 404 (source not found / cross-user),
   * 409 (name collision in the target project), 422 (validation).
   */
  async copyWorkflow(
    workflowId: string | number,
    payload: { name: string; target_project_id?: number | null }
  ): Promise<Workflow> {
    const response = await this.api.post(
      `/api/v1/orchestrations/${workflowId}/copy`,
      payload
    );
    return response.data;
  }

  // Workflow Steps
  async getWorkflowSteps(workflowId: string): Promise<WorkflowStep[]> {
    const response = await this.api.get(`/api/v1/orchestrations/${workflowId}/steps`);
    return response.data;
  }

  async createWorkflowStep(
    workflowId: string,
    data: Partial<WorkflowStep>
  ): Promise<WorkflowStep> {
    const response = await this.api.post(`/api/v1/orchestrations/${workflowId}/steps`, {
      ...data,
      orchestration_id: workflowId,
    });
    return response.data;
  }

  async updateWorkflowStep(
    stepId: string,
    data: Partial<WorkflowStep>
  ): Promise<WorkflowStep> {
    const response = await this.api.put(`/api/v1/orchestrations/steps/${stepId}`, data);
    return response.data;
  }

  async deleteWorkflowStep(stepId: string): Promise<void> {
    await this.api.delete(`/api/v1/orchestrations/steps/${stepId}`);
  }

  async convertUserPromptToAutoPrompt(workflowId: string, stepId: string): Promise<WorkflowStep> {
    const response = await this.api.post(
      `/api/v1/orchestrations/${workflowId}/steps/${stepId}/convert-to-auto-prompt`
    );
    return response.data;
  }

  /**
   * AC-1288 / AC-1289: Dry-run preview for conditional-to-filter conversion.
   *
   * Returns ``{convertible, reason?, preview?}`` without mutating any state.
   * Used by the NodeContextMenu "Convert to Filter" entry and the
   * ConditionBuilder advisor banner to gate the CTA.
   */
  async previewFilterConversion(
    workflowId: string | number,
    stepId: string | number,
  ): Promise<{
    convertible: boolean;
    reason?: string;
    preview?: {
      source_field: string;
      output_field: string;
      conditions: string[];
      on_empty: 'continue' | 'exit';
      element_class_name?: string | null;
    };
  }> {
    const response = await this.api.get(
      `/api/v1/orchestrations/${workflowId}/steps/${stepId}/filter-conversion-preview`
    );
    return response.data;
  }

  /**
   * AC-1288 / AC-1289: Convert a conditional step to a filter step.
   *
   * Atomic on the backend; on success the step has ``step_type='filter'`` and
   * a populated ``config`` FilterStepConfig. Old ``condition``/``on_true``/
   * ``on_false`` columns are nulled. 400 if not convertible (reason in body).
   */
  async convertConditionalToFilter(
    workflowId: string | number,
    stepId: string | number,
  ): Promise<WorkflowStep> {
    const response = await this.api.post(
      `/api/v1/orchestrations/${workflowId}/steps/${stepId}/convert-to-filter`
    );
    return response.data;
  }

  // Run Workflows
  async createRunInstance(workflowId: string): Promise<RunInstance> {
    const response = await this.api.post(`/api/v1/orchestrations/${workflowId}/create-run`);
    return response.data;
  }

  async runWorkflow(workflowId: string, runInstanceId?: string, inputMessage?: string): Promise<RunInstance> {
    // AC-1778: inputMessage (optional) overrides the initiator's prompt for
    // this run -- forwarded as a query param to POST /run.
    const params: Record<string, string> = {};
    if (runInstanceId) params.run_instance_id = runInstanceId;
    if (inputMessage) params.input_message = inputMessage;
    const response = await this.api.post(`/api/v1/orchestrations/${workflowId}/run`, null, { params });
    return response.data;
  }

  async getRunInstances(workflowId: string): Promise<RunInstance[]> {
    const response = await this.api.get(`/api/v1/orchestrations/${workflowId}/run-instances`);
    return response.data;
  }

  async getRunInstance(runInstanceId: string): Promise<RunInstance> {
    const response = await this.api.get(`/api/v1/orchestrations/run-instances/${runInstanceId}`);
    return response.data;
  }

  async getRunInstanceResults(runInstanceId: string, includeSession: boolean = true): Promise<RunResultsWithSession> {
    const response = await this.api.get(`/api/v1/run-instances/${runInstanceId}/results?include_session=${includeSession}`);
    return response.data;
  }

  async getSessionMessages(sessionKey: string, limit: number = 100, offset: number = 0): Promise<any[]> {
    const response = await this.api.get(`/api/v1/sessions/${sessionKey}/messages?limit=${limit}&offset=${offset}`);
    return response.data;
  }

  async updateRunResult(runResultId: string, data: { final_output: string }): Promise<RunResult> {
    const response = await this.api.put(`/api/v1/orchestrations/steps/run_result/${runResultId}`, data);
    return response.data;
  }

  // Alias for setting user prompt response (uses updateRunResult)
  async setUserPromptResponse(runResultId: string, userInput: string): Promise<RunResult> {
    return this.updateRunResult(runResultId, { final_output: userInput });
  }

  // Execution Control Methods
  async startWorkflowRun(workflowId: string): Promise<RunInstance> {
    const response = await this.api.post(`/api/v1/orchestrations/${workflowId}/start`);
    return response.data;
  }

  async pauseWorkflowRun(runInstanceId: string): Promise<void> {
    await this.api.post(`/api/v1/orchestrations/run_instances/${runInstanceId}/pause`);
  }

  async resumeWorkflowRun(runInstanceId: string, inputMessage?: string): Promise<RunInstance> {
    // AC-1794 (#3c): a paused user_prompt run is resumed with the user's
    // answer. When `inputMessage` is provided it is forwarded as the
    // `input_message` query param (the backend resume endpoint accepts it and
    // injects the answer as the prompt step's output before continuing). Plain
    // resume (no input) keeps the existing no-param behavior used by the editor
    // pause/resume control.
    const params: Record<string, string> = {};
    if (inputMessage !== undefined) params.input_message = inputMessage;
    const response = await this.api.post(
      `/api/v1/orchestrations/run-instances/${runInstanceId}/resume`,
      null,
      { params },
    );
    return response.data;
  }

  async stopWorkflowRun(runInstanceId: string): Promise<void> {
    await this.api.post(`/api/v1/orchestrations/run_instances/${runInstanceId}/stop`);
  }

  async submitFeedback(runInstanceId: string, feedback: any): Promise<void> {
    await this.api.post(`/api/v1/orchestrations/run_instances/${runInstanceId}/feedback`, feedback);
  }

  // Validation
  async validateWorkflow(workflowId: string): Promise<{
    isValid: boolean;
    errors: Array<{ message: string; type: string; nodeId?: string }>;
    warnings: Array<{ message: string; type: string; nodeId?: string }>;
  }> {
    const response = await this.api.post(`/api/v1/orchestrations/${workflowId}/validate`);
    return response.data;
  }

  // Import/Export
  async exportWorkflow(workflowId: string): Promise<Blob> {
    const response = await this.api.get(`/api/v1/orchestrations/${workflowId}/export`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async importWorkflow(projectId: string, file: File): Promise<Workflow> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.api.post(`/api/v1/projects/${projectId}/orchestrations/import`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // Health check
  async healthCheck(): Promise<{ message: string }> {
    const response = await this.api.get('/');
    return response.data;
  }

  // Generic request method for custom endpoints
  async request<T>(method: 'GET' | 'POST' | 'PUT' | 'DELETE', url: string, data?: any): Promise<T> {
    const response = await this.api.request({
      method,
      url,
      data,
    });
    return response.data;
  }

  setCurrentOperationId(projectId: string | null) {
    this.currentProjectId = projectId;
  }

  // Get allowed next step types
  async getNextStepTypes(prevStepType?: string): Promise<string[]> {
    const params = prevStepType ? { prev_step_type: prevStepType } : {};
    const response = await this.api.get('/api/v1/orchestrations/step-types/next', { params });
    return response.data;
  }

  // Get allowed intent-aware step types and intents based on previous step
  async getAllowedIntents(request?: AllowedIntentsRequest): Promise<AllowedStepIntentOption[]> {
    try {
      // Create cache key from request parameters
      const cacheKey = JSON.stringify({
        prev_step_type: request?.prev_step_type || null,
        prev_intent: request?.prev_intent || null,
        orchestration_id: request?.orchestration_id ?? null,
        endpoint: 'allowed-intents'
      });

      // Check cache first
      const cached = this.allowedIntentsCache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < this.allowedIntentsCacheTtl) {
        return cached.data;
      }

      const params: Record<string, string> = {};

      if (request?.prev_step_type) {
        params.prev_step_type = request.prev_step_type;
      }

      if (request?.prev_intent) {
        params.prev_intent = request.prev_intent;
      }

      if (request?.orchestration_id != null) {
        params.orchestration_id = String(request.orchestration_id);
      }

      console.log('[getAllowedIntents] Request params:', params);
      const response = await this.api.get('/api/v1/orchestrations/allowed-intents', { params });
      console.log('[getAllowedIntents] Response status:', response.status);
      console.log('[getAllowedIntents] Response data:', response.data);
      const data = response.data;

      // Cache the response
      this.allowedIntentsCache.set(cacheKey, {
        data,
        timestamp: Date.now()
      });

      return data;
    } catch (error) {
      console.error('[getAllowedIntents] Error occurred:', error);
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as any;
        console.error('[getAllowedIntents] Error response status:', axiosError.response?.status);
        console.error('[getAllowedIntents] Error response data:', JSON.stringify(axiosError.response?.data, null, 2));
        if (axiosError.response?.data?.detail) {
          console.error('[getAllowedIntents] Validation errors:', axiosError.response.data.detail);
        }
        console.error('[getAllowedIntents] Error response headers:', axiosError.response?.headers);
      }
      throw this.createApiError(error);
    }
  }

  // Get allowed intents with legacy support for backward compatibility
  async getAllowedIntentsWithLegacy(request?: AllowedIntentsRequest): Promise<AllowedStepIntentOption[]> {
    try {
      // Create cache key from request parameters
      const cacheKey = JSON.stringify({
        prev_step_type: request?.prev_step_type || null,
        prev_intent: request?.prev_intent || null,
        orchestration_id: request?.orchestration_id ?? null,
        endpoint: 'allowed-intents-legacy'
      });

      // Check cache first
      const cached = this.allowedIntentsCache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < this.allowedIntentsCacheTtl) {
        return cached.data;
      }

      const params: Record<string, string> = {};

      if (request?.prev_step_type) {
        params.prev_step_type = request.prev_step_type;
      }

      if (request?.prev_intent) {
        params.prev_intent = request.prev_intent;
      }

      if (request?.orchestration_id != null) {
        params.orchestration_id = String(request.orchestration_id);
      }

      const response = await this.api.get('/api/v1/orchestrations/allowed-intents/legacy', { params });
      const data = response.data;

      // Cache the response
      this.allowedIntentsCache.set(cacheKey, {
        data,
        timestamp: Date.now()
      });

      return data;
    } catch (error) {
      throw this.createApiError(error);
    }
  }

  // Enhanced async workflow methods
  
  // Create API error with enhanced information
  private createApiError(error: any): ApiError {
    const message = error.response?.data?.message || error.message || 'An unexpected error occurred';
    const code = error.response?.data?.code || error.code;
    const details = error.response?.data?.details || error.response?.data;
    const status = error.response?.status;
    
    // Determine if error is retryable
    const retryable = this.isRetryableError(error);
    
    return new ApiError(message, code, details, retryable, status);
  }

  // Check if error is retryable
  private isRetryableError(error: any): boolean {
    const status = error.response?.status;
    const code = error.code;
    
    // Network errors are retryable
    if (code === 'NETWORK_ERROR' || code === 'ECONNABORTED') {
      return true;
    }
    
    // Server errors (5xx) are retryable
    if (status >= 500) {
      return true;
    }
    
    // Rate limiting is retryable
    if (status === 429) {
      return true;
    }
    
    // Timeout errors are retryable
    if (code === 'ECONNRESET' || code === 'ETIMEDOUT') {
      return true;
    }
    
    return false;
  }

  // Enhanced request with retry logic
  async requestWithRetry<T>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    url: string,
    data?: any,
    options: {
      maxRetries?: number;
      timeout?: number;
      priority?: number;
    } = {}
  ): Promise<T> {
    const {
      maxRetries = this.retryConfig.maxRetries,
      timeout = this.requestTimeout,
      priority = 0,
    } = options;

    return new Promise((resolve, reject) => {
      const requestId = `req-${Date.now()}-${Math.random()}`;
      
      const queuedRequest: QueuedRequest = {
        id: requestId,
        request: async () => {
          const config = {
            method,
            url,
            data,
            timeout,
          };
          
          const response = await this.api.request(config);
          return response.data;
        },
        resolve,
        reject,
        retryCount: 0,
        maxRetries,
        priority,
      };

      this.requestQueue.push(queuedRequest);
      this.processRequestQueue();
    });
  }

  // Process request queue with concurrency control
  private async processRequestQueue(): Promise<void> {
    if (this.isProcessingQueue || this.activeRequests >= this.maxConcurrentRequests) {
      return;
    }

    this.isProcessingQueue = true;

    while (this.requestQueue.length > 0 && this.activeRequests < this.maxConcurrentRequests) {
      // Sort by priority (higher priority first)
      this.requestQueue.sort((a, b) => b.priority - a.priority);
      
      const queuedRequest = this.requestQueue.shift();
      if (!queuedRequest) break;

      this.activeRequests++;
      this.executeQueuedRequest(queuedRequest);
    }

    this.isProcessingQueue = false;
  }

  // Execute queued request with retry logic
  private async executeQueuedRequest(queuedRequest: QueuedRequest): Promise<void> {
    try {
      const result = await queuedRequest.request();
      queuedRequest.resolve(result);
    } catch (error: any) {
      const apiError = this.createApiError(error);
      
      if (apiError.retryable && queuedRequest.retryCount < queuedRequest.maxRetries) {
        queuedRequest.retryCount++;
        
        // Calculate delay with exponential backoff
        const delay = Math.min(
          this.retryConfig.baseDelay * Math.pow(this.retryConfig.backoffMultiplier, queuedRequest.retryCount - 1),
          this.retryConfig.maxDelay
        );
        
        setTimeout(() => {
          this.requestQueue.unshift(queuedRequest); // Add back to front of queue
          this.processRequestQueue();
        }, delay);
      } else {
        queuedRequest.reject(apiError);
      }
    } finally {
      this.activeRequests--;
      this.processRequestQueue(); // Process next requests
    }
  }

  // Batch request method for multiple projects
  async batchRequest<T>(requests: Array<{
    method: 'GET' | 'POST' | 'PUT' | 'DELETE';
    url: string;
    data?: any;
    priority?: number;
  }>): Promise<T[]> {
    const promises = requests.map(req =>
      this.requestWithRetry<T>(req.method, req.url, req.data, { priority: req.priority })
    );
    
    return Promise.all(promises);
  }

  // Enhanced workflow execution with real-time updates
  async startWorkflowWithUpdates(
    workflowId: string,
    onUpdate?: (update: any) => void
  ): Promise<RunInstance> {
    const runInstance = await this.startWorkflowRun(workflowId);
    
    // Set up polling for updates if no WebSocket available
    if (onUpdate && !window.WebSocket) {
      const pollInterval = setInterval(async () => {
        try {
          const updatedInstance = await this.getRunInstance(runInstance.run_instance_id);
          onUpdate({ type: 'status_update', data: updatedInstance });
          
          if (updatedInstance.status === 'completed' || updatedInstance.status === 'failed') {
            clearInterval(pollInterval);
          }
        } catch (error) {
          console.error('Polling error:', error);
        }
      }, 5000); // Increased from 2000ms to 5000ms for better performance
    }
    
    return runInstance;
  }

  // Enhanced real-time polling service for run results
  async pollRunResults(
    runInstanceId: string,
    options: {
      onNewResult?: (result: RunResult) => void;
      onStatusChange?: (status: RunStatus) => void;
      onComplete?: (response: RunResultsWithSession) => void;
      onError?: (error: string) => void;
      pollingInterval?: number;
      maxDuration?: number;
      enableProgressiveUpdates?: boolean;
    } = {}
  ): Promise<{
    stop: () => void;
    getCurrentResults: () => RunResult[];
    getCurrentStatus: () => RunStatus | null;
  }> {
    console.log('[DEBUG] pollRunResults called with runInstanceId:', runInstanceId, 'options:', options);
    try {
      const {
        onNewResult,
        onStatusChange,
        onComplete,
        onError,
        pollingInterval = 2000, // Increased from 1000ms to 2000ms default
        maxDuration = 300000, // 5 minutes default
        enableProgressiveUpdates = true
      } = options;

      console.log('[DEBUG] pollRunResults: Configuration:', {
        pollingInterval,
        maxDuration,
        enableProgressiveUpdates,
        hasOnNewResult: !!onNewResult,
        hasOnStatusChange: !!onStatusChange,
        hasOnComplete: !!onComplete,
        hasOnError: !!onError
      });

      let currentResults: RunResult[] = [];
      let currentStatus: RunStatus | null = null;
      let lastResultCount = 0;
      let lastStatus: RunStatus | null = null;
      let pollIntervalId: NodeJS.Timeout | null = null;
      let startTime = Date.now();
      let isStopped = false;
      let consecutiveErrors = 0;
      let currentPollInterval = pollingInterval;
      let lastActivityTime = Date.now();

      // Exponential backoff configuration
      const maxPollInterval = 30000; // 30 seconds max
      const minPollInterval = 1000; // 1 second min
      const backoffMultiplier = 1.5;
      const resetThreshold = 30000; // Reset backoff after 30 seconds of success

      const stop = () => {
        console.log('[DEBUG] pollRunResults: stop() called');
        isStopped = true;
        if (pollIntervalId) {
          clearInterval(pollIntervalId);
          pollIntervalId = null;
        }
      };

      const getCurrentResults = () => [...currentResults];
      const getCurrentStatus = () => currentStatus;

      const updatePollInterval = (success: boolean) => {
        if (success) {
          consecutiveErrors = 0;
          // Reset to base interval if we've had success for a while
          if (Date.now() - lastActivityTime > resetThreshold) {
            currentPollInterval = pollingInterval;
          }
        } else {
          consecutiveErrors++;
          // Exponential backoff with jitter
          const backoffTime = Math.min(
            currentPollInterval * Math.pow(backoffMultiplier, consecutiveErrors),
            maxPollInterval
          );
          // Add jitter (±20%) to prevent thundering herd
          const jitter = backoffTime * 0.2 * (Math.random() - 0.5);
          currentPollInterval = Math.max(backoffTime + jitter, minPollInterval);
        }
        
        // Restart polling with new interval
        if (pollIntervalId) {
          clearInterval(pollIntervalId);
        }
        pollIntervalId = setInterval(poll, currentPollInterval);
        console.log('[DEBUG] pollRunResults: Updated polling interval to', currentPollInterval, 'ms (consecutive errors:', consecutiveErrors, ')');
      };

      const poll = async () => {
        if (isStopped) {
          console.log('[DEBUG] pollRunResults: poll() called but isStopped is true, returning');
          return;
        }
        console.log('[DEBUG] pollRunResults: poll() tick for runInstanceId:', runInstanceId);
        try {
          // Check if we've exceeded max duration
          if (Date.now() - startTime > maxDuration) {
            console.log('[DEBUG] pollRunResults: Max duration exceeded, stopping');
            onError?.('Polling exceeded maximum duration');
            stop();
            return;
          }

          // Get current run instance status
          console.log('[DEBUG] pollRunResults: Fetching run instance status for:', runInstanceId);
          const runInstance = await this.getRunInstance(runInstanceId);
          const newStatus = runInstance.status as RunStatus;
          console.log('[DEBUG] pollRunResults: Run instance status:', newStatus, 'previous status:', lastStatus);

          // Check for status change
          if (newStatus !== lastStatus) {
            console.log('[DEBUG] pollRunResults: Status changed from', lastStatus, 'to', newStatus);
            lastStatus = newStatus;
            currentStatus = newStatus;
            lastActivityTime = Date.now();
            onStatusChange?.(newStatus);
          }

          // If completed, failed, stopped, or paused, get current results and stop
          // For paused status: polling stops to save resources while waiting for user input
          // Polling will resume when user submits input (via explicit startPolling call)
          if (newStatus === 'completed' || newStatus === 'failed' || newStatus === 'stopped' || newStatus === 'paused') {
            console.log('[DEBUG] pollRunResults: Status is', newStatus, ', getting final results and stopping');
            const finalResponse = await this.getRunInstanceResults(runInstanceId);
            console.log('[DEBUG] pollRunResults: Final results:', finalResponse.results);
            console.log('[DEBUG] pollRunResults: Session data:', {
              sessionMessages: finalResponse.session_messages?.length || 0,
              sessionInfoMap: Object.keys(finalResponse.session_info_map || {}).length
            });
            currentResults = finalResponse.results;
            onComplete?.(finalResponse);
            stop();
            return;
          }

          // Get current run results
          console.log('[DEBUG] pollRunResults: Fetching run results for:', runInstanceId);
          const response = await this.getRunInstanceResults(runInstanceId);
          const results = response.results;
          console.log('[DEBUG] pollRunResults: Got', results.length, 'results, previous count was', lastResultCount);
          
          // Check for new results
          if (results.length > lastResultCount) {
            const newResults = results.slice(lastResultCount);
            console.log('[DEBUG] pollRunResults: Found', newResults.length, 'new results:', newResults);
            lastResultCount = results.length;
            currentResults = results;
            lastActivityTime = Date.now();

            // Notify about new results
            if (enableProgressiveUpdates) {
              console.log('[DEBUG] pollRunResults: Progressive updates enabled, calling onNewResult for each new result');
              for (const result of newResults) {
                onNewResult?.(result);
              }
            }
          } else {
            console.log('[DEBUG] pollRunResults: No new results found');
          }

          // Success - update polling interval
          updatePollInterval(true);

        } catch (error) {
          console.error('[DEBUG] pollRunResults: Error in poll function:', error);

          // Handle 404 errors - this means the run instance doesn't exist
          if (error instanceof Error && error.message.includes('404')) {
            console.log('[DEBUG] pollRunResults: Run instance not found (404), stopping polling');
            onError?.('Run instance not found');
            stop();
            return;
          }

          // AC-1366: stop on 401. The axios interceptor handles refresh on
          // single requests, but if it cascades to here the refresh has
          // already failed. Continuing to poll wedges the spinner because
          // we never observe a terminal status. The store's stopPolling
          // post-reconcile will pick up the true status once auth recovers.
          const errMessage = error instanceof Error ? error.message : '';
          const status = (error as { response?: { status?: number } } | undefined)?.response?.status;
          if (status === 401 || errMessage.includes('401')) {
            console.log('[DEBUG] pollRunResults: 401 Unauthorized, stopping polling');
            onError?.('Unauthorized');
            stop();
            return;
          }

          // Handle connection/timeout errors specifically
          if (error instanceof Error && (
            error.message.includes('timeout') ||
            error.message.includes('connection') ||
            error.message.includes('QueuePool')
          )) {
            console.warn('[DEBUG] pollRunResults: Connection/timeout error detected, implementing backoff');
            onError?.(`Connection error: ${error.message}`);
          } else {
            onError?.(error instanceof Error ? error.message : 'Polling error');
          }

          // Update polling interval with backoff
          updatePollInterval(false);
        }
      };

      // Start polling with initial interval
      console.log('[DEBUG] pollRunResults: Setting up interval with', currentPollInterval, 'ms interval');
      pollIntervalId = setInterval(poll, currentPollInterval);
      
      // Initial poll
      console.log('[DEBUG] pollRunResults: Starting initial poll');
      await poll();

      return {
        stop,
        getCurrentResults,
        getCurrentStatus
      };

    } catch (error) {
      console.error('[DEBUG] pollRunResults: Failed to initialize polling:', error);
      if (options.onError) {
        options.onError(error instanceof Error ? error.message : 'Failed to initialize polling');
      }
      throw error;
    }
  }

  // Enhanced workflow execution with progressive result updates
  async executeWorkflowWithProgressiveUpdates(
    workflowId: string,
    options: {
      onStepStart?: (step: any) => void;
      onStepComplete?: (result: RunResult) => void;
      onStepError?: (result: RunResult) => void;
      onStatusChange?: (status: RunStatus) => void;
      onComplete?: (response: RunResultsWithSession) => void;
      onError?: (error: string) => void;
      onUserInputRequired?: (runResultId: string, message: string) => void;
      pollingInterval?: number;
      maxDuration?: number;
    } = {}
  ): Promise<{
    runInstance: RunInstance;
    stop: () => void;
    submitUserInput: (input: string) => Promise<void>;
  }> {
    const {
      onStepStart,
      onStepComplete,
      onStepError,
      onStatusChange,
      onComplete,
      onError,
      onUserInputRequired,
      pollingInterval = 1000,
      maxDuration = 300000
    } = options;

    // Start the workflow
    const runInstance = await this.runWorkflow(workflowId);
    
    let userInputRunResultId: string | null = null;
    let isWaitingForUserInput = false;

    // Set up polling
    const poller = await this.pollRunResults(runInstance.run_instance_id, {
      onNewResult: (result) => {
        // Check if this is a user prompt step waiting for input
        if (result.status === 'waiting_for_input' && !result.final_output) {
          isWaitingForUserInput = true;
          userInputRunResultId = result.run_result_id;
          onUserInputRequired?.(result.run_result_id, result.input_data?.message || 'Please provide input');
          return;
        }

        // Handle step completion
        if (result.status === 'completed') {
          onStepComplete?.(result);
        } else if (result.status === 'failed') {
          onStepError?.(result);
        }
      },
      onStatusChange,
      onComplete,
      onError,
      pollingInterval,
      maxDuration,
      enableProgressiveUpdates: true
    });

    const submitUserInput = async (input: string) => {
      if (!userInputRunResultId || !isWaitingForUserInput) {
        throw new Error('No user input required at this time');
      }

      try {
        await this.setUserPromptResponse(userInputRunResultId, input);
        isWaitingForUserInput = false;
        userInputRunResultId = null;

        // Continue execution
        await this.runWorkflow(workflowId, runInstance.run_instance_id);
      } catch (error) {
        onError?.(error instanceof Error ? error.message : 'Failed to submit user input');
        throw error;
      }
    };

    return {
      runInstance,
      stop: poller.stop,
      submitUserInput
    };
  }

  // Cancel all pending requests
  cancelAllRequests(): void {
    this.requestQueue.forEach(req => {
      req.reject(new ApiError('Request cancelled', 'CANCELLED'));
    });
    this.requestQueue = [];
  }

  // Get request queue status
  getRequestQueueStatus(): {
    pending: number;
    active: number;
    maxConcurrent: number;
  } {
    return {
      pending: this.requestQueue.length,
      active: this.activeRequests,
      maxConcurrent: this.maxConcurrentRequests,
    };
  }

  // Update configuration
  updateConfig(config: {
    maxConcurrentRequests?: number;
    requestTimeout?: number;
    retryConfig?: Partial<{
      maxRetries: number;
      baseDelay: number;
      maxDelay: number;
      backoffMultiplier: number;
    }>;
  }): void {
    if (config.maxConcurrentRequests) {
      this.maxConcurrentRequests = config.maxConcurrentRequests;
    }
    if (config.requestTimeout) {
      this.requestTimeout = config.requestTimeout;
    }
    if (config.retryConfig) {
      this.retryConfig = { ...this.retryConfig, ...config.retryConfig };
    }
  }

  // Run Instance Deletion Methods
  async deleteRunInstance(runInstanceId: string): Promise<void> {
    await this.api.delete(`/api/v1/orchestrations/run-instances/${runInstanceId}`);
  }

  async deleteAllRunInstancesForWorkflow(workflowId: string): Promise<{ message: string; deleted_count: number }> {
    const response = await this.api.delete(`/api/v1/orchestrations/${workflowId}/run-instances`);
    return response.data;
  }

  async deleteMultipleRunInstances(runInstanceIds: string[]): Promise<{ message: string; deleted_count: number }> {
    const requestData = { run_instance_ids: runInstanceIds.map(id => parseInt(id, 10)) };
    const response = await this.api.post('/api/v1/orchestrations/run-instances/bulk-delete', requestData);
    return response.data;
  }

  async deleteRunInstancesByDateRange(
    startDate: string,
    endDate: string,
    workflowId?: string
  ): Promise<{ message: string; deleted_count: number }> {
    const params: any = { start_date: startDate, end_date: endDate };
    if (workflowId) {
      params.orchestration_id = workflowId;
    }
    const response = await this.api.delete('/api/v1/orchestrations/run-instances/by-date-range', { params });
    return response.data;
  }

  // Activity Log API Methods
  async getRecentActivities(options: {
    skip?: number;
    limit?: number;
    activityTypes?: string[];
    statusFilter?: string;
    daysBack?: number;
    projectId?: number;
  } = {}): Promise<{
    activities: Array<{
      activity_log_id: number;
      user_id: number;
      activity_type: string;
      title: string;
      description?: string;
      status: string;
      activity_data?: any;
      created_at: string;
    }>;
    total_count: number;
    has_more: boolean;
  }> {
    const params = new URLSearchParams();
    if (options.skip !== undefined) params.append('skip', options.skip.toString());
    if (options.limit !== undefined) params.append('limit', options.limit.toString());
    if (options.activityTypes) params.append('activity_types', options.activityTypes.join(','));
    if (options.statusFilter) params.append('status_filter', options.statusFilter);
    if (options.daysBack !== undefined) params.append('days_back', options.daysBack.toString());
    if (options.projectId !== undefined) params.append('project_id', options.projectId.toString());

    return this.request('GET', `/api/v1/activities/recent?${params.toString()}`);
  }

  async getActivityStats(daysBack: number = 30): Promise<{
    total_activities: number;
    activities_by_type: Record<string, number>;
    activities_by_status: Record<string, number>;
    period_days: number;
  }> {
    return this.request('GET', `/api/v1/activities/stats?days_back=${daysBack}`);
  }

  async createActivityLog(data: {
    activity_type: string;
    title: string;
    description?: string;
    status?: string;
    activity_data?: any;
  }): Promise<{
    activity_log_id: number;
    user_id: number;
    activity_type: string;
    title: string;
    description?: string;
    status: string;
    activity_data?: any;
    created_at: string;
  }> {
    return this.request('POST', '/api/v1/activities/log', data);
  }

  async getActivityLog(activityLogId: number): Promise<{
    activity_log_id: number;
    user_id: number;
    activity_type: string;
    title: string;
    description?: string;
    status: string;
    activity_data?: any;
    created_at: string;
  }> {
    return this.request('GET', `/api/v1/activities/${activityLogId}`);
  }

  async updateActivityLog(activityLogId: number, data: {
    title?: string;
    description?: string;
    status?: string;
    activity_data?: any;
  }): Promise<{
    activity_log_id: number;
    user_id: number;
    activity_type: string;
    title: string;
    description?: string;
    status: string;
    activity_data?: any;
    created_at: string;
  }> {
    return this.request('PUT', `/api/v1/activities/${activityLogId}`, data);
  }

  async deleteActivityLog(activityLogId: number): Promise<void> {
    return this.request('DELETE', `/api/v1/activities/${activityLogId}`);
  }

  // AI Assistant Agent API Methods

  // Chat with assistant
  async chatWithAssistant(chatRequest: AssistantChatRequest): Promise<AssistantChatResponse> {
    const response = await this.api.post('/api/v1/assistant/chat', chatRequest);
    return response.data;
  }

  /**
   * Confirm a pending assistant plan so the backend executes it (AC-1644).
   * Returns the follow-up turn after the plan runs. A 409 means the plan changed
   * under the user (the preview is stale): the backend answers with a fresh
   * AssistantChatResponse carrying the updated/superseded preview, which we
   * RETURN instead of throwing so the UI can show a "plan changed" banner rather
   * than crash. Any other status code propagates as a throw.
   *
   * AC-1787 (defect fix): on the 409 branch we stamp `planChanged: true` on the
   * returned body. A 200 SUCCESS can ALSO carry a fresh `pending_action_preview`
   * (the journey's build-confirm legitimately surfaces the next-step schedule
   * card on success), so the call site cannot infer "superseded" from the mere
   * presence of a preview. The marker makes the 409 path unambiguous: only a real
   * conflict shows the "plan changed" banner; a clean next-step card does not.
   */
  async confirmAssistantAction(
    conversationId: number,
    actionId: number
  ): Promise<AssistantChatResponse> {
    try {
      const response = await this.api.post(
        `/api/v1/assistant/conversations/${conversationId}/actions/${actionId}/confirm`
      );
      return response.data;
    } catch (error: any) {
      if (error?.response?.status === 409 && error.response.data) {
        return { ...error.response.data, planChanged: true } as AssistantChatResponse;
      }
      throw error;
    }
  }

  /**
   * Decline a pending assistant plan so nothing is created (AC-1644). Returns the
   * decline acknowledgement; errors propagate to the caller.
   */
  async declineAssistantAction(
    conversationId: number,
    actionId: number
  ): Promise<{ success: boolean; status: string }> {
    const response = await this.api.post(
      `/api/v1/assistant/conversations/${conversationId}/actions/${actionId}/decline`
    );
    return response.data;
  }

  /**
   * Confirm a pending `propose_mcp_server` action and provision the remote MCP
   * server (AC-1738, epic AC-1732; backend AC-1737). Carries the chosen scope,
   * the owning project (when scope === 'project'), and any credentials entered
   * AFTER approval intent. Credentials are sent solely on this request and are
   * never logged. Returns the follow-up turn whose executed
   * `provision_mcp_server` action carries the new `mcp_server_id` / `tool_count`
   * (or `reused` on an idempotent duplicate hit). A 409 (plan no longer pending)
   * is returned as the response body so the UI can surface it rather than crash.
   * Backend: POST /assistant/conversations/{cid}/actions/{aid}/confirm-mcp
   */
  async confirmMcpProposal(
    conversationId: number,
    actionId: number,
    request: McpProvisionRequest
  ): Promise<AssistantChatResponse> {
    try {
      const response = await this.api.post(
        `/api/v1/assistant/conversations/${conversationId}/actions/${actionId}/confirm-mcp`,
        request
      );
      return response.data;
    } catch (error: any) {
      if (error?.response?.status === 409 && error.response.data) {
        return error.response.data as AssistantChatResponse;
      }
      throw error;
    }
  }

  /**
   * Decline a pending `propose_mcp_server` action so nothing is provisioned
   * (AC-1738; backend AC-1737). Decline-without-residue: zero MCPServer rows and
   * zero credentials are written; the action flips to `declined`. Returns the
   * decline acknowledgement turn.
   * Backend: POST /assistant/conversations/{cid}/actions/{aid}/decline-mcp
   */
  async declineMcpProposal(
    conversationId: number,
    actionId: number
  ): Promise<AssistantChatResponse> {
    const response = await this.api.post(
      `/api/v1/assistant/conversations/${conversationId}/actions/${actionId}/decline-mcp`
    );
    return response.data;
  }

  /**
   * Confirm a pending `configure_builtin_mcp` action and connect an unconfigured
   * built-in (e.g. Email) from inside the assistant chat (AC-1746, defect
   * AC-1743; backend AC-1745). Carries the built-in id + a
   * `SaveCredentialRequest`-shaped credentials payload that the backend reuses
   * verbatim via `BuiltinCredentialService.save_credentials` (encrypted).
   * Credentials are sent solely on this request and are never logged. Returns the
   * follow-up turn whose executed `configure_builtin_mcp` action carries the new
   * `credential_id` / `server_name`. A 409 (plan no longer pending) is returned as
   * the response body so the UI can surface it rather than crash.
   * Backend: POST /assistant/conversations/{cid}/actions/{aid}/confirm-builtin
   */
  async confirmBuiltinSetup(
    conversationId: number,
    actionId: number,
    request: McpConfigureBuiltinRequest
  ): Promise<AssistantChatResponse> {
    try {
      const response = await this.api.post(
        `/api/v1/assistant/conversations/${conversationId}/actions/${actionId}/confirm-builtin`,
        request
      );
      return response.data;
    } catch (error: any) {
      if (error?.response?.status === 409 && error.response.data) {
        return error.response.data as AssistantChatResponse;
      }
      throw error;
    }
  }

  /**
   * Decline a pending `configure_builtin_mcp` action so nothing is saved
   * (AC-1746; backend AC-1745). Decline-without-residue: zero
   * BuiltinServerCredential rows are written; the action flips to `declined`.
   * Returns the decline acknowledgement turn.
   * Backend: POST /assistant/conversations/{cid}/actions/{aid}/decline-builtin
   */
  async declineBuiltinSetup(
    conversationId: number,
    actionId: number
  ): Promise<AssistantChatResponse> {
    const response = await this.api.post(
      `/api/v1/assistant/conversations/${conversationId}/actions/${actionId}/decline-builtin`
    );
    return response.data;
  }

  /**
   * Confirm a pending `configure_schedule` action and create a recurring schedule
   * for the just-built email-summary workflow from inside the assistant chat
   * (AC-1790, epic AC-1787; backend AC-1788/AC-1789). Carries the final cadence
   * the user adjusted in the card (schedule_type + cron_expression OR
   * interval_minutes + timezone + is_enabled). The backend takes the
   * orchestration_id from the stored action_data (authoritative) and re-checks
   * ownership; this request's orchestration_id is sent for parity but is not
   * trusted server-side. Returns the follow-up turn whose executed
   * `configure_schedule` action carries the new schedule (or, idempotently, the
   * already-scheduled acknowledgement). A 409 (plan no longer pending) is returned
   * as the response body so the UI can surface it rather than crash.
   *
   * AC-1787 (defect fix): the 409 branch stamps `planChanged: true` so the call
   * site distinguishes a real superseded conflict from a SUCCESS turn that
   * legitimately carries a fresh pending_action_preview (parity with
   * confirmAssistantAction).
   * Backend: POST /assistant/conversations/{cid}/actions/{aid}/confirm-schedule
   */
  async confirmScheduleSetup(
    conversationId: number,
    actionId: number,
    request: ScheduleConfirmRequest
  ): Promise<AssistantChatResponse> {
    try {
      const response = await this.api.post(
        `/api/v1/assistant/conversations/${conversationId}/actions/${actionId}/confirm-schedule`,
        request
      );
      return response.data;
    } catch (error: any) {
      if (error?.response?.status === 409 && error.response.data) {
        return { ...error.response.data, planChanged: true } as AssistantChatResponse;
      }
      throw error;
    }
  }

  /**
   * Decline a pending `configure_schedule` action so no schedule is created
   * (AC-1790; backend AC-1789). Decline-without-residue: zero OrchestrationSchedule
   * rows are written; the action flips to `declined`. Returns the decline
   * acknowledgement turn.
   * Backend: POST /assistant/conversations/{cid}/actions/{aid}/decline-schedule
   */
  async declineScheduleSetup(
    conversationId: number,
    actionId: number
  ): Promise<AssistantChatResponse> {
    const response = await this.api.post(
      `/api/v1/assistant/conversations/${conversationId}/actions/${actionId}/decline-schedule`
    );
    return response.data;
  }

  // Assistant Conversations
  async createAssistantConversation(conversationData: AssistantConversationCreate): Promise<AssistantConversation> {
    const response = await this.api.post('/api/v1/assistant/conversations', conversationData);
    return response.data;
  }

  async getAssistantConversations(params?: {
    status?: string;
    project_id?: number;
  }): Promise<AssistantConversation[]> {
    const response = await this.api.get('/api/v1/assistant/conversations', { params });
    return response.data;
  }

  async getAssistantConversation(conversationId: number): Promise<AssistantConversationWithMessages> {
    const response = await this.api.get(`/api/v1/assistant/conversations/${conversationId}`);
    return response.data;
  }

  async updateAssistantConversation(
    conversationId: number,
    conversationUpdate: AssistantConversationUpdate
  ): Promise<AssistantConversation> {
    const response = await this.api.put(`/api/v1/assistant/conversations/${conversationId}`, conversationUpdate);
    return response.data;
  }

  async deleteAssistantConversation(conversationId: number): Promise<void> {
    await this.api.delete(`/api/v1/assistant/conversations/${conversationId}`);
  }

  // Assistant Actions
  async executeAssistantAction(actionId: number): Promise<any> {
    const response = await this.api.post(`/api/v1/assistant/actions/${actionId}/execute`);
    return response.data;
  }

  // Knowledge Base
  async createKnowledgeBase(knowledgeData: KnowledgeBaseCreate): Promise<KnowledgeBase> {
    const response = await this.api.post('/api/v1/knowledge/base', knowledgeData);
    return response.data;
  }

  async getKnowledgeBases(params?: {
    content_type?: string;
    project_id?: number;
  }): Promise<KnowledgeBase[]> {
    const response = await this.api.get('/api/v1/knowledge/base', { params });
    return response.data;
  }

  async getKnowledgeBase(knowledgeBaseId: number): Promise<KnowledgeBase> {
    const response = await this.api.get(`/api/v1/knowledge/base/${knowledgeBaseId}`);
    return response.data;
  }

  async updateKnowledgeBase(
    knowledgeBaseId: number,
    knowledgeUpdate: KnowledgeBaseUpdate
  ): Promise<KnowledgeBase> {
    const response = await this.api.put(`/api/v1/knowledge/base/${knowledgeBaseId}`, knowledgeUpdate);
    return response.data;
  }

  async deleteKnowledgeBase(knowledgeBaseId: number): Promise<void> {
    await this.api.delete(`/api/v1/knowledge/base/${knowledgeBaseId}`);
  }

  async searchKnowledgeBase(searchRequest: KnowledgeSearchRequest): Promise<KnowledgeSearchResult[]> {
    const response = await this.api.post('/api/v1/knowledge/search', searchRequest);
    return response.data;
  }

  async searchKnowledgeForAssistant(searchRequest: KnowledgeSearchRequest): Promise<KnowledgeSearchResult[]> {
    const response = await this.api.post('/api/v1/assistant/search-knowledge', searchRequest);
    return response.data;
  }

  // Instruction Assistant (AC-1189)
  async generateInstructions(data: InstructionGenerateRequest): Promise<InstructionAssistantResponse> {
    const response = await this.api.post('/api/v1/instruction-assistant/generate', data);
    return response.data;
  }

  async improveInstructions(data: InstructionImproveRequest): Promise<InstructionAssistantResponse> {
    const response = await this.api.post('/api/v1/instruction-assistant/improve', data);
    return response.data;
  }

  async refineInstructions(data: InstructionRefineRequest): Promise<InstructionAssistantResponse> {
    const response = await this.api.post('/api/v1/instruction-assistant/refine', data);
    return response.data;
  }

  /**
   * Upload documents to the knowledge base (AC-804)
   * @param files Array of files to upload
   * @param projectId Optional project ID to associate with uploaded documents
   * @param onProgress Optional callback for upload progress updates
   * @returns DocumentUploadResponse with results for each file
   */
  async uploadDocuments(
    files: File[],
    projectId?: number,
    onProgress?: (progress: number) => void
  ): Promise<DocumentUploadResponse> {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    if (projectId) {
      formData.append('project_id', projectId.toString());
    }

    const response = await this.api.post('/api/v1/knowledge/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (event) => {
        if (onProgress && event.total) {
          const progress = Math.round((event.loaded / event.total) * 100);
          onProgress(progress);
        }
      },
    });
    return response.data;
  }

  /**
   * Check for duplicate documents before uploading (AC-818)
   * @param filenames Array of filenames to check
   * @param projectId Optional project ID to scope the check
   * @returns CheckDuplicatesResponse with duplicate info per filename
   */
  async checkDuplicateDocuments(
    filenames: string[],
    projectId?: number
  ): Promise<CheckDuplicatesResponse> {
    const response = await this.api.post('/api/v1/knowledge/check-duplicates', {
      filenames,
      project_id: projectId,
    });
    return response.data;
  }

  /**
   * Get project documents grouped by source filename (AC-830)
   */
  async getOperationDocuments(projectId: number): Promise<ProjectDocumentsResponse> {
    const response = await this.api.get('/api/v1/knowledge/documents', {
      params: { project_id: projectId },
    });
    return response.data;
  }

  /**
   * Delete all chunks for a document by source filename (AC-830)
   */
  async deleteDocument(sourceFilename: string, projectId: number): Promise<{ message: string; chunks_deleted: number; source_filename: string }> {
    const response = await this.api.delete(`/api/v1/knowledge/documents/${encodeURIComponent(sourceFilename)}`, {
      params: { project_id: projectId },
    });
    return response.data;
  }

  async getOperationContext(projectId: number): Promise<{
    name: string;
    description: string;
    agent_count: number;
    orchestration_count: number;
    model_count: number;
    created_at: string;
    updated_at: string;
    recent_orchestrations: Array<{
      name: string;
      description: string;
      step_count: number;
    }>;
    project_id: number;
  }> {
    const response = await this.api.get(`/api/v1/assistant/project-context/${projectId}`);
    return response.data;
  }

  async getWorkflowContext(workflowId: number): Promise<{
    orchestration_id: number;
    name: string;
    description: string;
    project_id: number;
    project_name: string;
    created_at: string;
    updated_at: string;
    total_steps: number;
    step_types: Record<string, number>;
    agents_used_count: number;
    agents_used: number[];
    steps: Array<{
      step_number: number;
      step_id: number;
      type: string;
      label: string;
      description: string;
      agent_id?: number;
      agent_name?: string;
      has_conditions: boolean;
      execution_mode: string;
      fan_out: boolean;
      conditional_logic?: boolean;
      on_true_action?: any;
      on_false_action?: any;
    }>;
    execution_stats: {
      total_runs: number;
      successful_runs: number;
      failed_runs: number;
      pending_runs: number;
      running_runs: number;
      success_rate: number;
    };
    most_recent_run?: {
      run_id: number;
      status: string;
      created_at: string;
      current_step_id?: number;
    };
    summary: string;
    trace_enabled: boolean;
  }> {
    const response = await this.api.get(`/api/v1/assistant/orchestration-context/${workflowId}`);
    return response.data;
  }

  async bulkCreateKnowledgeBase(knowledgeItems: KnowledgeBaseCreate[]): Promise<KnowledgeBase[]> {
    const response = await this.api.post('/api/v1/knowledge/bulk', knowledgeItems);
    return response.data;
  }

  // Session Management Methods
  async clearRunInstanceSessions(
    workflowId: string,
    runInstanceId: string,
    request: ClearSessionRequest
  ): Promise<ClearSessionResponse> {
    const response = await this.api.delete(
      `/api/v1/sessions/orchestrations/${workflowId}/run-instances/${runInstanceId}/sessions`,
      { data: request }
    );
    return response.data;
  }

  async clearSession(sessionKey: string, confirm: boolean = false): Promise<ClearSessionResponse> {
    const response = await this.api.delete(`/api/v1/sessions/${sessionKey}/messages`, {
      params: { confirmation: confirm }
    });
    return response.data;
  }

  // Generic method for making GET requests
  async get(url: string, config?: any): Promise<AxiosResponse> {
    return await this.api.get(url, config);
  }

  // Agent Templates
  async getAgentTemplates(category?: string): Promise<AgentTemplateList> {
    const params = category ? { category } : {};
    const response = await this.api.get('/api/v1/templates/agents', { params });
    return response.data;
  }

  async getAgentTemplate(templateId: number): Promise<AgentTemplate> {
    const response = await this.api.get(`/api/v1/templates/agents/${templateId}`);
    return response.data;
  }

  async createAgentFromTemplate(
    templateId: number,
    projectId: number,
    customName?: string
  ): Promise<Agent> {
    const response = await this.api.post(
      `/api/v1/templates/agents/${templateId}/instantiate`,
      {
        project_id: projectId,
        name: customName
      }
    );
    return response.data;
  }

  // GoDaddy API Methods
  async storeGoDaddyCredentials(
    projectId: number,
    credentials: GoDaddyCredentialsCreate
  ): Promise<{ message: string; environment: string; encrypted: boolean }> {
    const response = await this.api.post(
      `/api/v1/godaddy/projects/${projectId}/credentials`,
      credentials
    );
    return response.data;
  }

  async getGoDaddyCredentialsStatus(projectId: number): Promise<GoDaddyCredentialsStatus> {
    const response = await this.api.get(`/api/v1/godaddy/projects/${projectId}/credentials`);
    return response.data;
  }

  async deleteGoDaddyCredentials(projectId: number): Promise<{ message: string }> {
    const response = await this.api.delete(`/api/v1/godaddy/projects/${projectId}/credentials`);
    return response.data;
  }

  async testGoDaddyConnection(
    projectId: number,
    credentials?: GoDaddyTestConnectionRequest,
    useSavedCredentials?: boolean
  ): Promise<GoDaddyTestConnectionResponse> {
    // Backend: backend/routers/godaddy.py
    // Router prefix: /godaddy
    // Route: /projects/{project_id}/test-connection
    // Full path: /api/v1/godaddy/projects/{project_id}/test-connection

    // Build request body based on mode
    let requestBody: any = {};

    if (useSavedCredentials) {
      // Test using saved credentials from database
      requestBody = {
        use_saved_credentials: true,
        environment: credentials?.environment || 'production'  // Use provided environment or default to production
      };
    } else if (credentials) {
      // Test using provided credentials
      requestBody = credentials;
    } else {
      throw new Error('Either credentials or useSavedCredentials must be provided');
    }

    const response = await this.api.post(
      `/api/v1/godaddy/projects/${projectId}/test-connection`,
      requestBody
    );
    return response.data;
  }

  // GoDaddy Tool Definitions - Single Source of Truth for tool descriptions
  async getGoDaddyToolDefinitions(): Promise<{
    provider: string;
    tools: Array<{
      type: string;
      name: string;
      description: string;
      input_description?: string;
      example_output: string;
      notes?: string;
    }>;
  }> {
    const response = await this.api.get('/api/v1/tools/godaddy/definitions');
    return response.data;
  }

  /**
   * Fetch RDAP tool definitions (AC-1437 / AC-1439).
   *
   * Mirrors `getGoDaddyToolDefinitions` -- both endpoints return the same
   * `ToolDefinitionResponse` shape on the backend (see
   * `backend/routers/tools.py`). The RDAP endpoint is intentionally public:
   * tool definitions are capability metadata, not user data, and RDAP itself
   * is credential-free so there is no per-project credentials endpoint pair
   * to guard.
   *
   * Consumers (TD AC-1434 section 6.2):
   *   - `RDAPToolSelector` (agent tool picker)
   *   - `ToolModal` (workflow node tool list, fresh descriptions)
   *
   * Errors are propagated unchanged so callers can distinguish 5xx /
   * network failures from successful empty responses, matching the
   * GoDaddy method's contract.
   */
  async getRDAPToolDefinitions(): Promise<RDAPToolDefinitionsResponse> {
    const response = await this.api.get('/api/v1/tools/rdap/definitions');
    return response.data;
  }

  // GoDaddy Purchase Rules Methods
  async getOperationPurchaseRules(
    projectId: number
  ): Promise<PurchaseRulesResponse> {
    const response = await this.api.get(
      `/api/v1/projects/${projectId}/purchase-rules`
    );
    return response.data;
  }

  async updateProjectPurchaseRules(
    projectId: number,
    rules: PurchaseRules
  ): Promise<PurchaseRulesResponse> {
    const response = await this.api.put(
      `/api/v1/projects/${projectId}/purchase-rules`,
      rules
    );
    return response.data;
  }

  // Spending Analytics Methods
  async getSpendingAnalyticsSummary(
    projectId: number,
    period: TimePeriod = 'all_time',
    startDate?: string,
    endDate?: string
  ): Promise<SpendingAnalyticsSummary> {
    const params = new URLSearchParams();
    params.append('period', period);
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);

    const response = await this.api.get(
      `/api/v1/godaddy/projects/${projectId}/analytics/summary?${params.toString()}`
    );
    return response.data;
  }

  async getSpendingTrends(
    projectId: number,
    period: TimePeriod = 'this_month',
    granularity?: Granularity,
    startDate?: string,
    endDate?: string
  ): Promise<SpendingTrendData> {
    const params = new URLSearchParams();
    params.append('period', period);
    if (granularity) params.append('granularity', granularity);
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);

    const response = await this.api.get(
      `/api/v1/godaddy/projects/${projectId}/analytics/trends?${params.toString()}`
    );
    return response.data;
  }

  async getTLDDistribution(
    projectId: number,
    period: TimePeriod = 'all_time',
    limit: number = 10,
    startDate?: string,
    endDate?: string
  ): Promise<TLDDistributionData> {
    const params = new URLSearchParams();
    params.append('period', period);
    params.append('limit', limit.toString());
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);

    const response = await this.api.get(
      `/api/v1/godaddy/projects/${projectId}/analytics/tld-distribution?${params.toString()}`
    );
    return response.data;
  }

  async exportSpendingAnalytics(
    projectId: number,
    startDate?: string,
    endDate?: string,
    status?: string,
    tld?: string
  ): Promise<Blob> {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (status) params.append('status', status);
    if (tld) params.append('tld', tld);

    const response = await this.api.get(
      `/api/v1/godaddy/projects/${projectId}/analytics/export?${params.toString()}`,
      { responseType: 'blob' }
    );
    return response.data;
  }

  // Model Registry Methods
  async getRegistryEnabledModels(projectId: number): Promise<RegistryEnabledModel[]> {
    const response = await this.api.get(`/api/v1/projects/${projectId}/models/registry-enabled`);
    return response.data.models;
  }

  async getRegistryConfig(modelId: number): Promise<RegistryConfigResponse> {
    const response = await this.api.get(`/api/v1/models/${modelId}/registry/config`);
    return response.data;
  }

  async updateRegistryConfig(
    modelId: number,
    config: RegistryConfigUpdate
  ): Promise<RegistryConfigResponse> {
    const response = await this.api.put(
      `/api/v1/models/${modelId}/registry/config`,
      config
    );
    return response.data;
  }

  async getRegistryEntries(
    modelId: number,
    params?: RegistryEntriesParams
  ): Promise<RegistryEntriesListResponse> {
    const queryParams = {
      page: params?.page ?? 1,
      page_size: params?.page_size ?? 50,
      ...params
    };
    const response = await this.api.get(`/api/v1/models/${modelId}/registry/entries`, {
      params: queryParams
    });
    return response.data;
  }

  async getRegistryEntry(
    modelId: number,
    entryId: string
  ): Promise<RegistryEntryResponse> {
    const response = await this.api.get(
      `/api/v1/models/${modelId}/registry/entries/${entryId}`
    );
    return response.data;
  }

  async getRegistryStats(modelId: number): Promise<RegistryStatsResponse> {
    const response = await this.api.get(`/api/v1/models/${modelId}/registry/stats`);
    return response.data;
  }

  async deleteAllRegistryEntries(modelId: number): Promise<{ deleted_count: number }> {
    const response = await this.api.delete(`/api/v1/models/${modelId}/registry/entries`);
    return response.data;
  }

  // AC-388: Get registry entries by run instance ID
  async getRegistryEntriesByRun(
    runInstanceId: number,
    params?: { page?: number; page_size?: number }
  ): Promise<RegistryEntriesListResponse> {
    const queryParams = {
      page: params?.page ?? 1,
      page_size: params?.page_size ?? 50
    };
    const response = await this.api.get(`/api/v1/registry/entries/by-run/${runInstanceId}`, {
      params: queryParams
    });
    return response.data;
  }

  // AC-416: Queue CRUD Methods
  async getQueues(projectId: string): Promise<Queue[]> {
    const response = await this.api.get(`/api/v1/projects/${projectId}/queues`);
    return response.data;
  }

  async getQueue(queueId: number): Promise<Queue> {
    const response = await this.api.get(`/api/v1/queues/${queueId}`);
    return response.data;
  }

  // AC-421: Create Queue Method
  // AC-460: Added max_retry_count and single_consumer_mode options
  async createQueue(projectId: string, data: {
    name: string;
    description?: string;
    data_model_id: number;
    max_retry_count?: number;
    single_consumer_mode?: boolean;
    priority_field?: string | null;
  }): Promise<Queue> {
    const response = await this.api.post(`/api/v1/projects/${projectId}/queues`, data);
    return response.data;
  }

  // AC-399: Circuit Breaker Methods
  async resetCircuitBreaker(queueId: number): Promise<any> {
    const response = await this.api.post(`/api/v1/queues/${queueId}/circuit-breaker/reset`);
    return response.data;
  }

  async getCircuitBreakerState(queueId: number): Promise<CircuitBreakerState> {
    const response = await this.api.get(`/api/v1/queues/${queueId}/circuit-breaker/state`);
    // Map backend snake_case to frontend camelCase
    return {
      state: response.data.circuit_state,
      enabled: response.data.circuit_breaker_enabled,
      opened_at: response.data.circuit_opened_at,
      reason: response.data.circuit_open_reason,
    };
  }

  // AC-460: Updated to use QueueUpdateSettings which includes single_consumer_mode
  async updateQueue(queueId: number, settings: QueueUpdateSettings): Promise<any> {
    const response = await this.api.put(`/api/v1/queues/${queueId}`, settings);
    return response.data;
  }

  async deleteQueue(queueId: number): Promise<void> {
    await this.api.delete(`/api/v1/queues/${queueId}`);
  }

  // AC-402: Queue Consumer Management Methods
  async getQueueConsumers(queueId: number): Promise<QueueConsumer[]> {
    const response = await this.api.get(`/api/v1/queues/${queueId}/consumers`);
    return response.data;
  }

  // AC-472: Get workflows configured to consume from a queue (by step config)
  async getQueueConsumerConfigs(
    queueId: number,
    excludeWorkflowId?: number
  ): Promise<QueueConsumerConfig[]> {
    const params = excludeWorkflowId
      ? { exclude_orchestration_id: excludeWorkflowId }
      : {};
    const response = await this.api.get(`/api/v1/queues/${queueId}/consumer-configs`, { params });
    return response.data;
  }

  async registerConsumer(queueId: number, request: ConsumerRegisterRequest): Promise<QueueConsumer> {
    const response = await this.api.post(`/api/v1/queues/${queueId}/consumers`, request);
    return response.data;
  }

  async unregisterConsumer(queueId: number, workflowId: number): Promise<void> {
    await this.api.delete(`/api/v1/queues/${queueId}/consumers/${workflowId}`);
  }

  async updateConsumerHeartbeat(queueId: number, workflowId: number): Promise<QueueConsumer> {
    const response = await this.api.put(`/api/v1/queues/${queueId}/consumers/${workflowId}/heartbeat`);
    return response.data;
  }

  // AC-435: Queue Items Methods
  // AC-1590: `search` is an optional case-insensitive substring filter
  // applied by the backend against the queue's configured
  // display_payload_fields (OR'd). Empty/whitespace-only values and queues
  // with no configured display fields are silently treated as no-filter.
  async getQueueItems(
    queueId: number,
    params?: { offset?: number; limit?: number; status?: string; search?: string },
  ): Promise<QueueItem[]> {
    const response = await this.api.get(`/api/v1/queues/${queueId}/items`, { params });
    return response.data;
  }

  async getQueueItem(queueId: number, itemId: string): Promise<QueueItem> {
    const response = await this.api.get(`/api/v1/queues/${queueId}/items/${itemId}`);
    return response.data;
  }

  async addQueueItem(queueId: number, data: { payload: Record<string, any>; priority?: number }): Promise<QueueItem> {
    const response = await this.api.post(`/api/v1/queues/${queueId}/items`, data);
    return response.data;
  }

  async deleteQueueItem(queueId: number, itemId: string): Promise<void> {
    await this.api.delete(`/api/v1/queues/${queueId}/items/${itemId}`);
  }

  async bulkDeleteQueueItems(queueId: number, itemIds: string[]): Promise<{ deleted_count: number }> {
    const response = await this.api.post(`/api/v1/queues/${queueId}/items/bulk-delete`, { item_ids: itemIds });
    return response.data;
  }

  async retryQueueItem(queueId: number, itemId: string): Promise<QueueItem> {
    const response = await this.api.post(`/api/v1/queues/${queueId}/items/${itemId}/retry`);
    return response.data;
  }

  async bulkRetryQueueItems(queueId: number, itemIds: string[]): Promise<{ retried_count: number }> {
    const response = await this.api.post(`/api/v1/queues/${queueId}/items/bulk-retry`, { item_ids: itemIds });
    return response.data;
  }

  async retryAllFailedItems(queueId: number): Promise<{ retried_count: number }> {
    const response = await this.api.post(`/api/v1/queues/${queueId}/retry-all`);
    return response.data;
  }

  async getQueueStats(queueId: number): Promise<QueueStats> {
    const response = await this.api.get(`/api/v1/queues/${queueId}/stats`);
    // Transform backend response to frontend QueueStats format
    const data = response.data;
    return {
      total: data.total_items,
      pending: data.counts_by_status?.PENDING || 0,
      processing: data.counts_by_status?.PROCESSING || 0,
      completed: data.counts_by_status?.COMPLETED || 0,
      failed: data.counts_by_status?.FAILED || 0,
    };
  }

  async purgeQueue(queueId: number): Promise<{ deleted_count: number }> {
    const response = await this.api.delete(`/api/v1/queues/${queueId}/items`);
    return response.data;
  }

  /**
   * AC-1478: Fetch the priority histogram for a queue.
   *
   * Returns aggregated bins (one per distinct priority value, or fixed-width
   * range buckets, depending on the server's `bin_strategy` decision). Never
   * returns row-level data.
   */
  async getQueuePriorityHistogram(
    queueId: number,
    status: HistogramStatus = 'PENDING',
    bins: number = 20,
  ): Promise<PriorityHistogramResponse> {
    const response = await this.api.get(
      `/api/v1/queues/${queueId}/priority-histogram`,
      { params: { status, bins } },
    );
    return response.data;
  }

  /**
   * AC-1479: Fetch the payload-field discovery for a queue. Server samples up
   * to `sample` recent items (default 100, capped at 1000 server-side) and
   * returns the discovered top-level + one-level-nested dot-paths along with
   * count / frequency / example metadata for each.
   */
  async getQueuePayloadFields(
    queueId: number,
    sample: number = 100,
  ): Promise<PayloadFieldsResponse> {
    const response = await this.api.get(
      `/api/v1/queues/${queueId}/payload-fields`,
      { params: { sample } },
    );
    return response.data;
  }

  // AC-450: Schedule API Methods
  /**
   * Fetch the schedule for a workflow. Returns null when the workflow has no
   * schedule (the backend returns 404 in that case, but an absent schedule is
   * normal - not an error worth logging). Any other error is re-thrown.
   */
  async getScheduleByWorkflow(workflowId: number): Promise<Schedule | null> {
    try {
      const response = await this.api.get(
        `/api/v1/schedules/orchestration/${workflowId}`,
        { _suppressErrorLog: true } as any
      );
      return response.data;
    } catch (err: any) {
      if (err?.response?.status === 404) {
        return null;
      }
      throw err;
    }
  }

  async createSchedule(data: ScheduleCreate): Promise<Schedule> {
    const response = await this.api.post('/api/v1/schedules', data);
    return response.data;
  }

  async updateSchedule(scheduleId: number, data: ScheduleUpdate): Promise<Schedule> {
    const response = await this.api.put(`/api/v1/schedules/${scheduleId}`, data);
    return response.data;
  }

  async deleteSchedule(scheduleId: number): Promise<void> {
    await this.api.delete(`/api/v1/schedules/${scheduleId}`);
  }

  async listSchedules(params?: { is_enabled?: boolean; limit?: number; offset?: number }): Promise<Schedule[]> {
    const response = await this.api.get('/api/v1/schedules', { params });
    return response.data;
  }

  async previewNextRuns(cronExpression: string, timezone: string, count: number = 3): Promise<SchedulePreviewRun[]> {
    const response = await this.api.post('/api/v1/schedules/preview-next-runs', null, {
      params: { cron_expression: cronExpression, timezone, count }
    });
    return response.data;
  }

  // ==========================================================================
  // NOTIFICATION METHODS (AC-537)
  // ==========================================================================

  /**
   * Get paginated list of notifications for the current user.
   */
  async getNotifications(params?: {
    skip?: number;
    limit?: number;
    is_read?: boolean;
    notification_type?: string;
    priority?: string;
  }): Promise<{
    notifications: any[];
    total_count: number;
    unread_count: number;
    has_more: boolean;
  }> {
    const response = await this.api.get('/api/v1/notifications', { params });
    return response.data;
  }

  /**
   * Poll for new notifications since a timestamp or ID.
   */
  async pollNotifications(params?: {
    since?: string;
    last_id?: number;
  }): Promise<{
    notifications: any[];
    unread_count: number;
    server_time: string;
  }> {
    const response = await this.api.get('/api/v1/notifications/poll', { params });
    return response.data;
  }

  /**
   * Get unread notification count.
   */
  async getUnreadNotificationCount(): Promise<{ unread_count: number }> {
    const response = await this.api.get('/api/v1/notifications/unread-count');
    return response.data;
  }

  /**
   * Create a notification (primarily for testing).
   */
  async createNotification(data: {
    notification_type: string;
    title: string;
    priority?: string;
    message?: string | null;
    action_url?: string | null;
    metadata?: Record<string, unknown> | null;
    activity_log_id?: number | null;
  }): Promise<any> {
    const response = await this.api.post('/api/v1/notifications', data);
    return response.data;
  }

  /**
   * Mark a single notification as read.
   */
  async markNotificationRead(notificationId: number): Promise<any> {
    const response = await this.api.put(`/api/v1/notifications/${notificationId}/read`);
    return response.data;
  }

  /**
   * Mark all notifications as read.
   */
  async markAllNotificationsRead(): Promise<{ marked_count: number }> {
    const response = await this.api.put('/api/v1/notifications/mark-all-read');
    return response.data;
  }

  /**
   * Delete a notification.
   */
  async deleteNotification(notificationId: number): Promise<void> {
    await this.api.delete(`/api/v1/notifications/${notificationId}`);
  }

  // ==========================================================================
  // VERSION CONTROL METHODS (AC-570)
  // ==========================================================================

  // -------------------------------------------------------------------------
  // Checkpoint Methods
  // -------------------------------------------------------------------------

  /**
   * Get checkpoints for a project.
   */
  async getCheckpoints(
    projectId: number | string,
    params?: {
      include_auto?: boolean;
      limit?: number;
      offset?: number;
    }
  ): Promise<CheckpointListResponse> {
    const response = await this.api.get(
      `/api/v1/projects/${projectId}/checkpoints`,
      { params }
    );
    return response.data;
  }

  /**
   * Create a new checkpoint.
   */
  async createCheckpoint(
    projectId: number | string,
    data: CheckpointCreate
  ): Promise<CheckpointSummary> {
    const response = await this.api.post(
      `/api/v1/projects/${projectId}/checkpoints`,
      data
    );
    return response.data;
  }

  /**
   * Get a single checkpoint with full snapshot data.
   */
  async getCheckpoint(
    projectId: number | string,
    checkpointId: number
  ): Promise<CheckpointDetail> {
    const response = await this.api.get(
      `/api/v1/projects/${projectId}/checkpoints/${checkpointId}`
    );
    return response.data;
  }

  /**
   * Delete a checkpoint.
   */
  async deleteCheckpoint(
    projectId: number | string,
    checkpointId: number
  ): Promise<void> {
    await this.api.delete(
      `/api/v1/projects/${projectId}/checkpoints/${checkpointId}`
    );
  }

  /**
   * Restore a checkpoint.
   */
  async restoreCheckpoint(
    projectId: number | string,
    checkpointId: number,
    data: { create_pre_restore_checkpoint?: boolean }
  ): Promise<RestoreResponse> {
    const response = await this.api.post(
      `/api/v1/projects/${projectId}/checkpoints/${checkpointId}/restore`,
      data
    );
    return response.data;
  }

  /**
   * Compare two checkpoints.
   */
  async compareCheckpoints(
    projectId: number | string,
    fromId: number,
    toId: number
  ): Promise<CompareResponse> {
    const response = await this.api.get(
      `/api/v1/projects/${projectId}/checkpoints/compare`,
      {
        params: {
          from: fromId,
          to: toId,
        },
      }
    );
    return response.data;
  }

  // -------------------------------------------------------------------------
  // Changes Methods
  // -------------------------------------------------------------------------

  /**
   * Get changes for a project.
   */
  async getChanges(
    projectId: number | string,
    params?: ChangeFilters
  ): Promise<ChangeListResponse> {
    const response = await this.api.get(
      `/api/v1/projects/${projectId}/changes`,
      { params }
    );
    return response.data;
  }

  /**
   * Get a single change with full state data.
   */
  async getChange(
    projectId: number | string,
    changeId: number
  ): Promise<ChangeDetail> {
    const response = await this.api.get(
      `/api/v1/projects/${projectId}/changes/${changeId}`
    );
    return response.data;
  }

  /**
   * Re-apply a change.
   */
  async reapplyChange(
    projectId: number | string,
    changeId: number
  ): Promise<ReapplyResponse> {
    const response = await this.api.post(
      `/api/v1/projects/${projectId}/changes/${changeId}/reapply`
    );
    return response.data;
  }

  /**
   * Undo a change by applying the previous_state.
   */
  async undoChange(
    projectId: number | string,
    changeId: number
  ): Promise<UndoResponse> {
    const response = await this.api.post(
      `/api/v1/projects/${projectId}/changes/${changeId}/undo`
    );
    return response.data;
  }

  /**
   * Revert an entity to the state it had at a specific change.
   *
   * This undoes all changes that occurred AFTER the specified change
   * for that entity, effectively "rewinding" the entity to that point.
   *
   * Technical Design Reference: docs/technical/TDD-AC-592-entity-revert.md
   * Ticket: AC-592
   *
   * @param projectId - Project ID the change belongs to
   * @param changeId - Change ID to revert TO (entity restored to this state)
   * @returns RevertToResponse with count and IDs of reverted changes
   */
  async revertToChange(
    projectId: number | string,
    changeId: number
  ): Promise<RevertToResponse> {
    const response = await this.api.post(
      `/api/v1/projects/${projectId}/changes/${changeId}/revert-to`
    );
    return response.data;
  }

  // -------------------------------------------------------------------------
  // Entity History Methods
  // -------------------------------------------------------------------------

  /**
   * Get change history for a specific entity.
   */
  async getEntityHistory(
    entityType: ChangeEntityType,
    entityId: number,
    limit?: number
  ): Promise<{ changes: ChangeDetail[]; total_count?: number; has_more?: boolean }> {
    // Map entity type to the correct endpoint path (pluralize)
    const entityPath = `${entityType}s`; // agent -> agents, model -> models, workflow -> orchestrations
    const response = await this.api.get(
      `/api/v1/${entityPath}/${entityId}/history`,
      {
        params: limit ? { limit } : undefined,
      }
    );
    return response.data;
  }

  // -------------------------------------------------------------------------
  // Retention Settings Methods
  // -------------------------------------------------------------------------

  /**
   * Get retention settings for a project.
   */
  async getRetentionSettings(
    projectId: number | string
  ): Promise<RetentionSettings> {
    const response = await this.api.get(
      `/api/v1/projects/${projectId}/settings/retention`
    );
    return response.data;
  }

  /**
   * Update retention settings for a project.
   */
  async updateRetentionSettings(
    projectId: number | string,
    settings: RetentionSettingsUpdate
  ): Promise<RetentionSettings> {
    const response = await this.api.put(
      `/api/v1/projects/${projectId}/settings/retention`,
      settings
    );
    return response.data;
  }

  // ==========================================================================
  // TOKEN USAGE METHODS (AC-604)
  // ==========================================================================

  /**
   * Get token usage summary for the current user.
   * Backend: backend/routers/token_usage.py
   * Route: GET /api/v1/token-usage/summary
   */
  async getUsageSummary(params?: UsageQueryParams): Promise<TokenUsageSummary> {
    const response = await this.api.get('/api/v1/token-usage/summary', { params });
    return response.data;
  }

  /**
   * Get token usage breakdown by AI model.
   * Backend: backend/routers/token_usage.py
   * Route: GET /api/v1/token-usage/by-model
   */
  async getUsageByModel(params?: UsageQueryParams): Promise<TokenUsageByModelResponse> {
    const response = await this.api.get('/api/v1/token-usage/by-model', { params });
    return response.data;
  }

  /**
   * Get token usage breakdown by AI provider.
   * Backend: backend/routers/token_usage.py
   * Route: GET /api/v1/token-usage/by-provider
   */
  async getUsageByProvider(params?: UsageQueryParams): Promise<TokenUsageByProviderResponse> {
    const response = await this.api.get('/api/v1/token-usage/by-provider', { params });
    return response.data;
  }

  /**
   * Get token usage breakdown by orchestration.
   * Backend: backend/routers/token_usage.py
   * Route: GET /api/v1/token-usage/by-orchestration
   */
  async getUsageByWorkflow(params?: HierarchicalUsageQueryParams): Promise<TokenUsageByWorkflowResponse> {
    const response = await this.api.get('/api/v1/token-usage/by-orchestration', { params });
    return response.data;
  }

  /**
   * Get token usage breakdown by source type.
   * Backend: backend/routers/token_usage.py
   * Route: GET /api/v1/token-usage/by-source
   */
  async getUsageBySource(params?: UsageQueryParams): Promise<TokenUsageBySourceResponse> {
    const response = await this.api.get('/api/v1/token-usage/by-source', { params });
    return response.data;
  }

  /**
   * Get token usage trends over time.
   * Backend: backend/routers/token_usage.py
   * Route: GET /api/v1/token-usage/trends
   */
  async getUsageTrends(params?: UsageQueryParams): Promise<TokenUsageTrend> {
    const response = await this.api.get('/api/v1/token-usage/trends', { params });
    return response.data;
  }

  /**
   * Get token usage breakdown by project.
   * Backend: backend/routers/token_usage.py
   * Route: GET /api/v1/token-usage/by-project
   *
   * Related Ticket: AC-632 (Wiring)
   */
  async getUsageByProject(params?: UsageQueryParams): Promise<TokenUsageByProjectResponse> {
    const response = await this.api.get('/api/v1/token-usage/by-project', { params });
    return response.data;
  }

  /**
   * Get token usage breakdown by agent.
   * Backend: backend/routers/token_usage.py
   * Route: GET /api/v1/token-usage/by-agent
   *
   * Related Ticket: AC-632 (Wiring)
   */
  async getUsageByAgent(params?: HierarchicalUsageQueryParams): Promise<TokenUsageByAgentResponse> {
    const response = await this.api.get('/api/v1/token-usage/by-agent', { params });
    return response.data;
  }

  /**
   * Export token usage data as CSV.
   * Backend: backend/routers/token_usage.py
   * Route: GET /api/v1/token-usage/export
   */
  async exportUsageCsv(params?: UsageQueryParams): Promise<Blob> {
    const response = await this.api.get('/api/v1/token-usage/export', {
      params,
      responseType: 'blob',
    });
    return response.data;
  }

  // ==========================================================================
  // BUDGET METHODS (AC-604)
  // ==========================================================================

  /**
   * Get the current user's budget configuration.
   * Backend: backend/routers/budgets.py
   * Route: GET /api/v1/budgets/me
   */
  async getBudget(): Promise<UserBudget | null> {
    const response = await this.api.get('/api/v1/budgets/me');
    return response.data ?? null;
  }

  /**
   * Create a new budget for the current user.
   * Backend: backend/routers/budgets.py
   * Route: POST /api/v1/budgets/me
   */
  async createBudget(config: BudgetConfig): Promise<UserBudget> {
    const response = await this.api.post('/api/v1/budgets/me', config);
    return response.data;
  }

  /**
   * Update the current user's budget configuration.
   * Backend: backend/routers/budgets.py
   * Route: PUT /api/v1/budgets/me
   */
  async updateBudget(config: Partial<BudgetConfig>): Promise<UserBudget> {
    const response = await this.api.put('/api/v1/budgets/me', config);
    return response.data;
  }

  /**
   * Delete the current user's budget.
   * Backend: backend/routers/budgets.py
   * Route: DELETE /api/v1/budgets/me
   */
  async deleteBudget(): Promise<void> {
    await this.api.delete('/api/v1/budgets/me');
  }

  /**
   * Get the current user's budget status (current spend, projections).
   * Backend: backend/routers/budgets.py
   * Route: GET /api/v1/budgets/me/status
   */
  async getBudgetStatus(): Promise<BudgetStatus> {
    const response = await this.api.get('/api/v1/budgets/me/status');
    return response.data;
  }

  // ==========================================================================
  // TUTORIAL METHODS (AC-667)
  // ==========================================================================

  /**
   * Get the current user's tutorial progress.
   * Backend: backend/routers/tutorial.py
   * Route: GET /api/v1/users/me/tutorial
   */
  async getTutorialProgress(tutorialId?: string): Promise<TutorialProgressResponse> {
    const params = tutorialId ? { tutorial_id: tutorialId } : {};
    const response = await this.api.get('/api/v1/users/me/tutorial', { params });
    return response.data;
  }

  /**
   * Update the current user's tutorial progress.
   * Backend: backend/routers/tutorial.py
   * Route: PUT /api/v1/users/me/tutorial
   */
  async updateTutorialProgress(
    data: TutorialProgressUpdate,
    tutorialId?: string
  ): Promise<TutorialProgressResponse> {
    const params = tutorialId ? { tutorial_id: tutorialId } : {};
    const response = await this.api.put('/api/v1/users/me/tutorial', data, { params });
    return response.data;
  }

  /**
   * Restart the tutorial from the beginning.
   * Backend: backend/routers/tutorial.py
   * Route: POST /api/v1/users/me/tutorial/restart
   */
  async restartTutorial(tutorialId?: string): Promise<TutorialProgressResponse> {
    const params = tutorialId ? { tutorial_id: tutorialId } : {};
    const response = await this.api.post('/api/v1/users/me/tutorial/restart', null, { params });
    return response.data;
  }

  // ==========================================================================
  // MCP (Model Context Protocol) METHODS (AC-779)
  // ==========================================================================

  /**
   * List all MCP servers configured for a project.
   * Backend: backend/routers/mcp.py
   * Route: GET /api/v1/mcp/projects/{project_id}/servers
   */
  async listMCPServers(projectId: number): Promise<MCPServer[]> {
    const response = await this.api.get(`/api/v1/mcp/projects/${projectId}/servers`);
    return response.data;
  }

  /**
   * Get a specific MCP server by ID.
   * Backend: backend/routers/mcp.py
   * Route: GET /api/v1/mcp/projects/{project_id}/servers/{server_id}
   */
  async getMCPServer(projectId: number, serverId: number): Promise<MCPServer> {
    const response = await this.api.get(`/api/v1/mcp/projects/${projectId}/servers/${serverId}`);
    return response.data;
  }

  /**
   * Create a new MCP server configuration.
   * Backend: backend/routers/mcp.py
   * Route: POST /api/v1/mcp/projects/{project_id}/servers
   */
  async createMCPServer(projectId: number, data: MCPServerCreate): Promise<MCPServer> {
    const response = await this.api.post(`/api/v1/mcp/projects/${projectId}/servers`, data);
    return response.data;
  }

  /**
   * Update an existing MCP server configuration.
   * Backend: backend/routers/mcp.py
   * Route: PUT /api/v1/mcp/projects/{project_id}/servers/{server_id}
   */
  async updateMCPServer(projectId: number, serverId: number, data: MCPServerUpdate): Promise<MCPServer> {
    const response = await this.api.put(`/api/v1/mcp/projects/${projectId}/servers/${serverId}`, data);
    return response.data;
  }

  /**
   * Delete an MCP server configuration.
   * Backend: backend/routers/mcp.py
   * Route: DELETE /api/v1/mcp/projects/{project_id}/servers/{server_id}
   */
  async deleteMCPServer(projectId: number, serverId: number): Promise<void> {
    await this.api.delete(`/api/v1/mcp/projects/${projectId}/servers/${serverId}`);
  }

  /**
   * Test connection to an MCP server.
   * Backend: backend/routers/mcp.py
   * Route: POST /api/v1/mcp/projects/{project_id}/servers/{server_id}/test
   *
   * For new servers (not yet saved), pass the config directly.
   * For existing servers, pass the server_id.
   */
  async testMCPConnection(
    projectId: number,
    serverIdOrConfig: number | MCPServerCreate
  ): Promise<MCPTestConnectionResponse> {
    if (typeof serverIdOrConfig === 'number') {
      // Test existing server by ID
      const response = await this.api.post(
        `/api/v1/mcp/projects/${projectId}/servers/${serverIdOrConfig}/test`
      );
      return response.data;
    } else {
      // Test new server configuration (not yet saved)
      const response = await this.api.post(
        `/api/v1/mcp/projects/${projectId}/servers/test`,
        serverIdOrConfig
      );
      return response.data;
    }
  }

  /**
   * Discover tools from an MCP server.
   * Backend: backend/routers/mcp.py
   * Route: POST /api/v1/mcp/projects/{project_id}/servers/{server_id}/discover-tools
   */
  async discoverMCPTools(projectId: number, serverId: number): Promise<MCPToolInfo[]> {
    const response = await this.api.post(
      `/api/v1/mcp/projects/${projectId}/servers/${serverId}/discover-tools`
    );
    return response.data;
  }

  /**
   * Get available MCP server templates.
   * Backend: backend/routers/mcp.py
   * Route: GET /api/v1/mcp/templates
   * Ticket: AC-781
   */
  async getMCPServerTemplates(): Promise<MCPServerTemplate[]> {
    const response = await this.api.get('/api/v1/mcp/templates');
    return response.data;
  }

  // ==========================================================================
  // User-Level MCP Server Methods (AC-785)
  // ==========================================================================

  /**
   * List all user-level MCP servers for the current user.
   * User-level servers are available across all projects.
   * Backend: backend/routers/mcp.py
   * Route: GET /api/v1/mcp/servers
   * Ticket: AC-788
   */
  async listUserLevelMCPServers(): Promise<MCPServer[]> {
    const response = await this.api.get('/api/v1/mcp/servers');
    return response.data;
  }

  /**
   * Create a new user-level MCP server configuration.
   * Backend: backend/routers/mcp.py
   * Route: POST /api/v1/mcp/servers
   * Ticket: AC-788
   */
  async createUserLevelMCPServer(data: MCPServerCreate): Promise<MCPServer> {
    const response = await this.api.post('/api/v1/mcp/servers', data);
    return response.data;
  }

  /**
   * Get a specific user-level MCP server by ID.
   * Backend: backend/routers/mcp.py
   * Route: GET /api/v1/mcp/servers/{server_id}
   * Ticket: AC-788
   */
  async getUserLevelMCPServer(serverId: number): Promise<MCPServer> {
    const response = await this.api.get(`/api/v1/mcp/servers/${serverId}`);
    return response.data;
  }

  /**
   * Update a user-level MCP server configuration.
   * Backend: backend/routers/mcp.py
   * Route: PUT /api/v1/mcp/servers/{server_id}
   * Ticket: AC-788
   */
  async updateUserLevelMCPServer(serverId: number, data: MCPServerUpdate): Promise<MCPServer> {
    const response = await this.api.put(`/api/v1/mcp/servers/${serverId}`, data);
    return response.data;
  }

  /**
   * Delete a user-level MCP server configuration.
   * Backend: backend/routers/mcp.py
   * Route: DELETE /api/v1/mcp/servers/{server_id}
   * Ticket: AC-788
   */
  async deleteUserLevelMCPServer(serverId: number): Promise<void> {
    await this.api.delete(`/api/v1/mcp/servers/${serverId}`);
  }

  /**
   * Test connection to a user-level MCP server.
   * Backend: backend/routers/mcp.py
   * Route: POST /api/v1/mcp/servers/{server_id}/test
   * Ticket: AC-788
   */
  async testUserLevelMCPConnection(
    serverIdOrConfig: number | MCPServerCreate
  ): Promise<MCPTestConnectionResponse> {
    if (typeof serverIdOrConfig === 'number') {
      // Test existing server by ID
      const response = await this.api.post(`/api/v1/mcp/servers/${serverIdOrConfig}/test`);
      return response.data;
    } else {
      // Test new configuration before saving
      const response = await this.api.post('/api/v1/mcp/servers/test', serverIdOrConfig);
      return response.data;
    }
  }

  /**
   * Discover tools available on a user-level MCP server.
   * Backend: backend/routers/mcp.py
   * Route: POST /api/v1/mcp/servers/{server_id}/discover-tools
   * Ticket: AC-788
   */
  async discoverUserLevelMCPTools(serverId: number): Promise<MCPToolInfo[]> {
    const response = await this.api.post(`/api/v1/mcp/servers/${serverId}/discover-tools`);
    return response.data;
  }

  /**
   * List all MCP servers available in a project context.
   * Returns both user-level and project-level servers.
   * Backend: backend/routers/mcp.py
   * Route: GET /api/v1/mcp/projects/{project_id}/servers/available
   * Ticket: AC-788
   */
  async listAvailableMCPServers(projectId: number): Promise<MCPServersAvailableResponse> {
    const response = await this.api.get(`/api/v1/mcp/projects/${projectId}/servers/available`);
    return response.data;
  }

  // =========================================================================
  // Social / Sharing (AC-856)
  // =========================================================================

  async followUser(userId: number): Promise<FollowResponse> {
    const response = await this.api.post(`/api/v1/social/users/${userId}/follow`);
    return response.data;
  }

  async unfollowUser(userId: number): Promise<void> {
    await this.api.delete(`/api/v1/social/users/${userId}/follow`);
  }

  async getUserProfile(userId: number): Promise<UserPublicProfile> {
    const response = await this.api.get(`/api/v1/social/users/${userId}/profile`);
    return response.data;
  }

  async getUserFollowers(userId: number, skip: number = 0, limit: number = 50): Promise<UserListResponse> {
    const response = await this.api.get(`/api/v1/social/users/${userId}/followers`, { params: { skip, limit } });
    return response.data;
  }

  async getUserFollowing(userId: number, skip: number = 0, limit: number = 50): Promise<UserListResponse> {
    const response = await this.api.get(`/api/v1/social/users/${userId}/following`, { params: { skip, limit } });
    return response.data;
  }

  async searchUsers(query: string, skip: number = 0, limit: number = 20): Promise<UserListResponse> {
    const response = await this.api.get('/api/v1/social/users/search', { params: { q: query, skip, limit } });
    return response.data;
  }

  async getExploreProjects(sort: string = 'recent', skip: number = 0, limit: number = 20, userId?: number, query?: string): Promise<SharedProjectListResponse> {
    const params: Record<string, string | number> = { sort, skip, limit };
    if (userId !== undefined) {
      params.user_id = userId;
    }
    if (query) {
      params.q = query;
    }
    const response = await this.api.get('/api/v1/explore/projects', { params });
    return response.data;
  }

  async getFollowingFeed(skip: number = 0, limit: number = 20): Promise<SharedProjectListResponse> {
    const response = await this.api.get('/api/v1/explore/feed', { params: { skip, limit } });
    return response.data;
  }

  async getSharedProject(projectId: number): Promise<SharedProjectDetail> {
    const response = await this.api.get(`/api/v1/explore/projects/${projectId}`);
    return response.data;
  }

  async getSharedProjectWorkflows(projectId: number): Promise<SharedWorkflowSummary[]> {
    const response = await this.api.get(`/api/v1/explore/projects/${projectId}/orchestrations`);
    return response.data;
  }

  async getSharedWorkflow(projectId: number, workflowId: number): Promise<SharedWorkflowDetail> {
    const response = await this.api.get(`/api/v1/explore/projects/${projectId}/orchestrations/${workflowId}`);
    return response.data;
  }

  async getSharedProjectAgents(projectId: number): Promise<SharedAgentSummary[]> {
    const response = await this.api.get(`/api/v1/explore/projects/${projectId}/agents`);
    return response.data;
  }

  async getSharedAgent(projectId: number, agentId: number): Promise<SharedAgentSummary> {
    const response = await this.api.get(`/api/v1/explore/projects/${projectId}/agents/${agentId}`);
    return response.data;
  }

  async getSharedProjectModels(projectId: number): Promise<SharedModelSummary[]> {
    const response = await this.api.get(`/api/v1/explore/projects/${projectId}/models`);
    return response.data;
  }

  async recordProjectView(projectId: number): Promise<void> {
    await this.api.post(`/api/v1/explore/projects/${projectId}/view`);
  }

  // =========================================================================
  // Phase 2: Run-from-Shared & Project Versioning (AC-856)
  // =========================================================================

  async runSharedWorkflow(projectId: number, workflowId: number, inputMessage?: string): Promise<RunInstance> {
    const response = await this.api.post(
      `/api/v1/explore/projects/${projectId}/orchestrations/${workflowId}/run`,
      inputMessage ? { input_message: inputMessage } : null
    );
    return response.data;
  }

  async publishVersion(projectId: number, versionName: string, description?: string): Promise<PublishedVersionSummary> {
    const response = await this.api.post(`/api/v1/projects/${projectId}/versions`, {
      version_name: versionName,
      description,
    });
    return response.data;
  }

  async listProjectVersions(projectId: number): Promise<PublishedVersionListResponse> {
    const response = await this.api.get(`/api/v1/projects/${projectId}/versions`);
    return response.data;
  }

  async listSharedProjectVersions(projectId: number): Promise<PublishedVersionListResponse> {
    const response = await this.api.get(`/api/v1/explore/projects/${projectId}/versions`);
    return response.data;
  }

  async getSharedVersionDetail(projectId: number, checkpointId: number): Promise<PublishedVersionDetail> {
    const response = await this.api.get(`/api/v1/explore/projects/${projectId}/versions/${checkpointId}`);
    return response.data;
  }

  async getLatestSharedVersion(projectId: number): Promise<PublishedVersionDetail> {
    const response = await this.api.get(`/api/v1/explore/projects/${projectId}/versions/latest`);
    return response.data;
  }

  async getRunResults(runInstanceId: number | string): Promise<any[]> {
    const response = await this.api.get(`/api/v1/run-instances/${runInstanceId}/results`);
    return response.data?.results || response.data || [];
  }

  // =========================================================================
  // Teams (AC-888, Phase 3)
  // =========================================================================

  async createTeam(data: TeamCreate): Promise<TeamResponse> {
    const response = await this.api.post('/api/v1/teams', data);
    return response.data;
  }

  async listMyTeams(): Promise<TeamListResponse> {
    const response = await this.api.get('/api/v1/teams');
    return response.data;
  }

  async discoverTeams(search?: string, skip: number = 0, limit: number = 20): Promise<TeamListResponse> {
    const params: Record<string, string | number> = { skip, limit };
    if (search) {
      params.search = search;
    }
    const response = await this.api.get('/api/v1/teams/discover', { params });
    return response.data;
  }

  async getTeamDetail(teamId: number): Promise<TeamDetail> {
    const response = await this.api.get(`/api/v1/teams/${teamId}`);
    return response.data;
  }

  async updateTeam(teamId: number, data: TeamUpdate): Promise<TeamResponse> {
    const response = await this.api.put(`/api/v1/teams/${teamId}`, data);
    return response.data;
  }

  async deleteTeam(teamId: number): Promise<void> {
    await this.api.delete(`/api/v1/teams/${teamId}`);
  }

  async listTeamMembers(teamId: number): Promise<TeamMemberListResponse> {
    const response = await this.api.get(`/api/v1/teams/${teamId}/members`);
    return response.data;
  }

  async removeTeamMember(teamId: number, userId: number): Promise<void> {
    await this.api.delete(`/api/v1/teams/${teamId}/members/${userId}`);
  }

  async inviteToTeam(teamId: number, username: string): Promise<TeamInvitation> {
    const response = await this.api.post(`/api/v1/teams/${teamId}/invitations`, { username });
    return response.data;
  }

  async listTeamInvitations(teamId: number): Promise<TeamInvitationListResponse> {
    const response = await this.api.get(`/api/v1/teams/${teamId}/invitations`);
    return response.data;
  }

  async listMyInvitations(): Promise<TeamInvitationListResponse> {
    const response = await this.api.get('/api/v1/invitations');
    return response.data;
  }

  async acceptInvitation(invitationId: number): Promise<TeamInvitation> {
    const response = await this.api.post(`/api/v1/invitations/${invitationId}/accept`);
    return response.data;
  }

  async declineInvitation(invitationId: number): Promise<TeamInvitation> {
    const response = await this.api.post(`/api/v1/invitations/${invitationId}/decline`);
    return response.data;
  }

  async listTeamProjects(teamId: number): Promise<SharedProjectListResponse> {
    const response = await this.api.get(`/api/v1/teams/${teamId}/projects`);
    return response.data;
  }

  // ==========================================================================
  // Admin / Allowlist (AC-915)
  // ==========================================================================

  async getAllowlist(params?: { search?: string; skip?: number; limit?: number }): Promise<{ entries: AllowlistEntry[]; total: number }> {
    const response = await this.api.get('/api/v1/admin/allowlist', { params });
    return response.data;
  }

  async addAllowlistEmail(email: string): Promise<AllowlistEntry> {
    const response = await this.api.post('/api/v1/admin/allowlist', { email });
    return response.data;
  }

  async addAllowlistEmailsBulk(emails: string[]): Promise<{ added: number; duplicates: number; entries: AllowlistEntry[] }> {
    const response = await this.api.post('/api/v1/admin/allowlist/bulk', { emails });
    return response.data;
  }

  async removeAllowlistEmail(entryId: number): Promise<{ success: boolean }> {
    const response = await this.api.delete(`/api/v1/admin/allowlist/${entryId}`);
    return response.data;
  }

  async getRegistrationMode(): Promise<{ mode: string }> {
    const response = await this.api.get('/api/v1/admin/settings/registration-mode');
    return response.data;
  }

  async setRegistrationMode(mode: string): Promise<{ mode: string }> {
    const response = await this.api.put('/api/v1/admin/settings/registration-mode', { mode });
    return response.data;
  }

  async getAdminUsers(params?: { search?: string; is_admin?: boolean; skip?: number; limit?: number }): Promise<{ users: AdminUserSummary[]; total: number }> {
    const response = await this.api.get('/api/v1/admin/users', { params });
    return response.data;
  }

  async setAdminUserRole(userId: number, isAdmin: boolean): Promise<AdminUserSummary> {
    const response = await this.api.put(`/api/v1/admin/users/${userId}/role`, { is_admin: isAdmin });
    return response.data;
  }

  async deleteAdminUser(userId: number): Promise<{ success: boolean; message: string }> {
    const response = await this.api.delete(`/api/v1/admin/users/${userId}`);
    return response.data;
  }

  // ==========================================================================
  // Admin Token Usage (Admin Portal)
  // ==========================================================================

  async getAdminUsageSummary(params?: UsageQueryParams): Promise<AdminPlatformUsageSummary> {
    const response = await this.api.get('/api/v1/admin/token-usage/summary', { params });
    return response.data;
  }

  async getAdminUsageByUser(params?: UsageQueryParams & { sort_by?: string; search?: string; skip?: number; limit?: number }): Promise<AdminUserUsageListResponse> {
    const response = await this.api.get('/api/v1/admin/token-usage/by-user', { params });
    return response.data;
  }

  async getAdminUserUsageSummary(userId: number, params?: UsageQueryParams): Promise<TokenUsageSummary> {
    const response = await this.api.get(`/api/v1/admin/token-usage/users/${userId}/summary`, { params });
    return response.data;
  }

  async getAdminUserUsageByModel(userId: number, params?: UsageQueryParams): Promise<TokenUsageByModelResponse> {
    const response = await this.api.get(`/api/v1/admin/token-usage/users/${userId}/by-model`, { params });
    return response.data;
  }

  async getAdminUserUsageByProvider(userId: number, params?: UsageQueryParams): Promise<TokenUsageByProviderResponse> {
    const response = await this.api.get(`/api/v1/admin/token-usage/users/${userId}/by-provider`, { params });
    return response.data;
  }

  async getAdminUserUsageBySource(userId: number, params?: UsageQueryParams): Promise<TokenUsageBySourceResponse> {
    const response = await this.api.get(`/api/v1/admin/token-usage/users/${userId}/by-source`, { params });
    return response.data;
  }

  async getAdminUserUsageByWorkflow(userId: number, params?: HierarchicalUsageQueryParams): Promise<TokenUsageByWorkflowResponse> {
    const response = await this.api.get(`/api/v1/admin/token-usage/users/${userId}/by-orchestration`, { params });
    return response.data;
  }

  async getAdminUserUsageTrends(userId: number, params?: UsageQueryParams): Promise<TokenUsageTrend> {
    const response = await this.api.get(`/api/v1/admin/token-usage/users/${userId}/trends`, { params });
    return response.data;
  }

  async getAdminUserUsageByProject(userId: number, params?: UsageQueryParams): Promise<TokenUsageByProjectResponse> {
    const response = await this.api.get(`/api/v1/admin/token-usage/users/${userId}/by-project`, { params });
    return response.data;
  }

  async getAdminUserUsageByAgent(userId: number, params?: HierarchicalUsageQueryParams): Promise<TokenUsageByAgentResponse> {
    const response = await this.api.get(`/api/v1/admin/token-usage/users/${userId}/by-agent`, { params });
    return response.data;
  }

  async getAdminUserUsageExport(userId: number, params?: UsageQueryParams): Promise<Blob> {
    const response = await this.api.get(`/api/v1/admin/token-usage/users/${userId}/export`, {
      params,
      responseType: 'blob',
    });
    return response.data;
  }

  // WhatsApp Integration (AC-1050)
  async whatsAppConnect(): Promise<WhatsAppConnectionResponse> {
    const response = await this.api.post('/api/v1/whatsapp/connect');
    return response.data;
  }

  async whatsAppDisconnect(): Promise<{ status: string }> {
    const response = await this.api.post('/api/v1/whatsapp/disconnect');
    return response.data;
  }

  async whatsAppGetStatus(): Promise<WhatsAppStatus> {
    const response = await this.api.get('/api/v1/whatsapp/status');
    return response.data;
  }

  async whatsAppGetQR(): Promise<WhatsAppQRResponse> {
    const response = await this.api.get('/api/v1/whatsapp/qr');
    return response.data;
  }

  async whatsAppRequestPairingCode(phoneNumber: string): Promise<WhatsAppPairingCodeResponse> {
    const response = await this.api.post('/api/v1/whatsapp/pairing-code', { phone_number: phoneNumber });
    return response.data;
  }

  async whatsAppSendTest(message?: string): Promise<{ sent: boolean }> {
    const response = await this.api.post('/api/v1/whatsapp/test', { message });
    return response.data;
  }

  async whatsAppGetNotificationPreferences(): Promise<WhatsAppNotificationPreferences> {
    const response = await this.api.get('/api/v1/whatsapp/notification-preferences');
    return response.data;
  }

  async whatsAppUpdateNotificationPreferences(prefs: WhatsAppNotificationPreferencesUpdate): Promise<WhatsAppNotificationPreferences> {
    const response = await this.api.put('/api/v1/whatsapp/notification-preferences', prefs);
    return response.data;
  }

  async whatsAppToggleOrchestrationNotify(orchestrationId: number, enabled: boolean): Promise<WhatsAppNotificationPreferences> {
    const response = await this.api.put(`/api/v1/whatsapp/orchestrations/${orchestrationId}/notify`, { enabled });
    return response.data;
  }

  // Slack Inbound ChatOps (Epic AC-1819 / AC-1823)
  async slackGetConfig(): Promise<SlackConfigResponse> {
    const response = await this.api.get('/api/v1/slack/config');
    return response.data;
  }

  async slackSaveConfig(teamId: string, signingSecret: string): Promise<SlackConfigResponse> {
    const response = await this.api.post('/api/v1/slack/config', { team_id: teamId, signing_secret: signingSecret });
    return response.data;
  }

  async slackLinkStart(): Promise<SlackLinkStartResponse> {
    const response = await this.api.post('/api/v1/slack/link/start');
    return response.data;
  }

  async slackLinkStatus(): Promise<SlackLinkStatusResponse> {
    const response = await this.api.get('/api/v1/slack/link/status');
    return response.data;
  }

  // ==========================================================================
  // Built-in Email / MCP Credentials (AC-1108)
  // ==========================================================================

  async listBuiltinCredentials(builtinServerId: number): Promise<BuiltinCredentialStatus[]> {
    const response = await this.api.get(`/api/v1/mcp/builtin-credentials/${builtinServerId}`);
    return response.data;
  }

  // Backwards compat alias
  async getBuiltinCredentialStatus(builtinServerId: number): Promise<BuiltinCredentialStatus> {
    const list = await this.listBuiltinCredentials(builtinServerId);
    if (list.length > 0) return list[0];
    return {
      has_credentials: false,
      credential_id: null,
      provider: null,
      instance_label: null,
      email: null,
      is_active: false,
      last_tested_at: null,
      last_test_success: null,
      created_at: null,
    };
  }

  async saveBuiltinCredentials(builtinServerId: number, data: SaveCredentialRequest): Promise<BuiltinCredentialStatus> {
    const response = await this.api.post(`/api/v1/mcp/builtin-credentials/${builtinServerId}`, data);
    return response.data;
  }

  async updateBuiltinCredentials(builtinServerId: number, credentialId: number, data: SaveCredentialRequest): Promise<BuiltinCredentialStatus> {
    const response = await this.api.put(`/api/v1/mcp/builtin-credentials/${builtinServerId}/${credentialId}`, data);
    return response.data;
  }

  async getCredentialDependents(builtinServerId: number, credentialId: number): Promise<{dependent_agents: Array<{agent_id: number, agent_name: string}>}> {
    const response = await this.api.get(`/api/v1/mcp/builtin-credentials/${builtinServerId}/${credentialId}/dependents`);
    return response.data;
  }

  async deleteBuiltinCredentials(builtinServerId: number, credentialId: number): Promise<void> {
    await this.api.delete(`/api/v1/mcp/builtin-credentials/${builtinServerId}/${credentialId}`);
  }

  async testBuiltinConnection(builtinServerId: number, credentialId: number | null, data: SaveCredentialRequest): Promise<TestConnectionResult> {
    // For new credentials (not yet saved), use credential_id 0 as placeholder
    const cid = credentialId || 0;
    const response = await this.api.post(`/api/v1/mcp/builtin-credentials/${builtinServerId}/${cid}/test`, data);
    return response.data;
  }

  async getEmailProviders(): Promise<EmailProviderPreset[]> {
    const response = await this.api.get('/api/v1/mcp/email-providers');
    return response.data;
  }

  /**
   * AC-1807 (epic AC-1803). Fetch the Slack guided-setup preset (token label,
   * `xoxb-` prefix, help steps/url/why, env var) that drives the Slack branch of
   * McpConfigureBuiltinCard. Public, no auth -- mirrors getEmailProviders.
   * Backend: GET /api/v1/mcp/slack-setup
   */
  async getSlackSetup(): Promise<SlackSetupPreset> {
    const response = await this.api.get('/api/v1/mcp/slack-setup');
    return response.data;
  }
}

// Create singleton instance
const apiService = new ApiService();

export { apiService };
export default apiService;

// Export individual methods for easier testing
export const {
  login,
  register,
  getCurrentUser,
  getProjects,
  getOperation,
  createProject,
  updateProject,
  deleteProject,
  getAgents,
  getAllAgents,
  getAgent,
  createAgent,
  updateAgent,
  deleteAgent,
  getModels,
  getAllModels,
  getLLMCatalog,
  // Assistant model selection (AC-1616)
  getAssistantModels,
  getAssistantModelPreference,
  setAssistantModelPreference,
  // System mode (AC-1619)
  getSystemMode,
  // BYOK (AC-1493)
  listApiKeys,
  createApiKey,
  updateApiKey,
  deleteApiKey,
  testApiKey,
  getModel,
  createModel,
  updateModel,
  deleteModel,
  getWorkflows,
  getAllWorkflows,
  getWorkflow,
  createWorkflow,
  updateWorkflow,
  getDeliveryChannelAvailability,
  deleteWorkflow,
  getWorkflowSteps,
  createWorkflowStep,
  updateWorkflowStep,
  deleteWorkflowStep,
  convertUserPromptToAutoPrompt,
  runWorkflow,
  getRunInstances,
  getRunInstance,
  getRunInstanceResults,
  getSessionMessages,
  updateRunResult,
  validateWorkflow,
  exportWorkflow,
  importWorkflow,
  healthCheck,
  getNextStepTypes,
  deleteRunInstance,
  deleteAllRunInstancesForWorkflow,
  deleteMultipleRunInstances,
  deleteRunInstancesByDateRange,
  getRecentActivities,
  getActivityStats,
  createActivityLog,
  getActivityLog,
  updateActivityLog,
  deleteActivityLog,
  chatWithAssistant,
  confirmAssistantAction,
  declineAssistantAction,
  confirmMcpProposal,
  declineMcpProposal,
  confirmBuiltinSetup,
  declineBuiltinSetup,
  confirmScheduleSetup,
  declineScheduleSetup,
  createAssistantConversation,
  getAssistantConversations,
  getAssistantConversation,
  updateAssistantConversation,
  deleteAssistantConversation,
  executeAssistantAction,
  createKnowledgeBase,
  getKnowledgeBases,
  getKnowledgeBase,
  updateKnowledgeBase,
  deleteKnowledgeBase,
  searchKnowledgeBase,
  searchKnowledgeForAssistant,
  getOperationContext,
  getWorkflowContext,
  bulkCreateKnowledgeBase,
  clearRunInstanceSessions,
  clearSession,
  getAgentTemplates,
  getAgentTemplate,
  createAgentFromTemplate,
  storeGoDaddyCredentials,
  getGoDaddyCredentialsStatus,
  deleteGoDaddyCredentials,
  testGoDaddyConnection,
  getGoDaddyToolDefinitions,
  getRDAPToolDefinitions,
  getOperationPurchaseRules,
  updateProjectPurchaseRules,
  getSpendingAnalyticsSummary,
  getSpendingTrends,
  getTLDDistribution,
  exportSpendingAnalytics,
  getRegistryEnabledModels,
  getRegistryConfig,
  updateRegistryConfig,
  getRegistryEntries,
  getRegistryEntry,
  getRegistryStats,
  deleteAllRegistryEntries,
  getRegistryEntriesByRun,
  getQueues,
  getQueue,
  createQueue,
  resetCircuitBreaker,
  getCircuitBreakerState,
  updateQueue,
  deleteQueue,
  getQueueConsumers,
  getQueueConsumerConfigs,
  registerConsumer,
  unregisterConsumer,
  updateConsumerHeartbeat,
  getQueueItems,
  getQueueItem,
  addQueueItem,
  deleteQueueItem,
  bulkDeleteQueueItems,
  retryQueueItem,
  bulkRetryQueueItems,
  retryAllFailedItems,
  getQueueStats,
  purgeQueue,
  getQueuePriorityHistogram,
  getQueuePayloadFields,
  getScheduleByWorkflow,
  createSchedule,
  updateSchedule,
  deleteSchedule,
  listSchedules,
  previewNextRuns,
  getNotifications,
  pollNotifications,
  getUnreadNotificationCount,
  createNotification,
  markNotificationRead,
  markAllNotificationsRead,
  deleteNotification,
  // Version Control (AC-570)
  getCheckpoints,
  createCheckpoint,
  getCheckpoint,
  deleteCheckpoint,
  restoreCheckpoint,
  compareCheckpoints,
  getChanges,
  getChange,
  reapplyChange,
  getEntityHistory,
  getRetentionSettings,
  updateRetentionSettings,
  // Token Usage (AC-604)
  getUsageSummary,
  getUsageByModel,
  getUsageByProvider,
  getUsageByWorkflow,
  getUsageBySource,
  getUsageTrends,
  exportUsageCsv,
  // Hierarchical Cost Breakdown (AC-622)
  getUsageByProject,
  getUsageByAgent,
  // Budgets (AC-604)
  getBudget,
  createBudget,
  updateBudget,
  deleteBudget,
  getBudgetStatus,
  // Tutorial (AC-667)
  getTutorialProgress,
  updateTutorialProgress,
  restartTutorial,
  // MCP (AC-778, AC-781)
  listMCPServers,
  getMCPServer,
  createMCPServer,
  updateMCPServer,
  deleteMCPServer,
  testMCPConnection,
  discoverMCPTools,
  getMCPServerTemplates,
  // User-Level MCP (AC-785)
  listUserLevelMCPServers,
  createUserLevelMCPServer,
  getUserLevelMCPServer,
  updateUserLevelMCPServer,
  deleteUserLevelMCPServer,
  testUserLevelMCPConnection,
  discoverUserLevelMCPTools,
  listAvailableMCPServers,
  // Social / Sharing (AC-856)
  followUser,
  unfollowUser,
  getUserProfile,
  getUserFollowers,
  getUserFollowing,
  searchUsers,
  getExploreProjects,
  getFollowingFeed,
  getSharedProject,
  getSharedProjectWorkflows,
  getSharedWorkflow,
  getSharedProjectAgents,
  getSharedAgent,
  getSharedProjectModels,
  recordProjectView,
  // Phase 2 (AC-856)
  runSharedWorkflow,
  publishVersion,
  listProjectVersions,
  listSharedProjectVersions,
  getSharedVersionDetail,
  getLatestSharedVersion,
  getRunResults,
  // Teams (AC-888)
  createTeam,
  listMyTeams,
  discoverTeams,
  getTeamDetail,
  updateTeam,
  deleteTeam,
  listTeamMembers,
  removeTeamMember,
  inviteToTeam,
  listTeamInvitations,
  listMyInvitations,
  acceptInvitation,
  declineInvitation,
  listTeamProjects,
  // Admin / Allowlist (AC-915)
  getAllowlist,
  addAllowlistEmail,
  addAllowlistEmailsBulk,
  removeAllowlistEmail,
  getRegistrationMode,
  setRegistrationMode,
  getAdminUsers,
  setAdminUserRole,
  getAdminUsageSummary,
  getAdminUsageByUser,
  getAdminUserUsageSummary,
  getAdminUserUsageByModel,
  getAdminUserUsageByProvider,
  getAdminUserUsageBySource,
  getAdminUserUsageByWorkflow,
  getAdminUserUsageTrends,
  getAdminUserUsageByProject,
  getAdminUserUsageByAgent,
  getAdminUserUsageExport,
  // WhatsApp Integration (AC-1050)
  whatsAppConnect,
  whatsAppDisconnect,
  whatsAppGetStatus,
  whatsAppGetQR,
  whatsAppRequestPairingCode,
  whatsAppSendTest,
  whatsAppGetNotificationPreferences,
  whatsAppUpdateNotificationPreferences,
  whatsAppToggleOrchestrationNotify,
  // Built-in Email / MCP Credentials (AC-1108)
  listBuiltinCredentials,
  getBuiltinCredentialStatus,
  saveBuiltinCredentials,
  updateBuiltinCredentials,
  deleteBuiltinCredentials,
  testBuiltinConnection,
  getEmailProviders,
  getSlackSetup,
  // Instruction Assistant (AC-1189)
  generateInstructions,
  improveInstructions,
  refineInstructions,
} = apiService;
